import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useAuth } from "./context/AuthContext.js";
import { GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import "./WebsiteBuilder.css";
import LivePreview from "./components/LivePreview.js";
import {
  createZipAndDownload,
  buildStandaloneHtmlFromParts,
  buildStaticZipFiles,
  buildViteReactZipFiles,
} from "./utils/exportZip.js";
import { getApiBase } from "./backendProbe.js";

// IMPORTANT: don't load Stripe.js at startup (it can be blocked by adblock / CSP and crash the app).
// We lazy-load only when the user actually tries to checkout.

export default function WebsiteBuilder({
  initialPrompt = "",
  onSendToComputerPreview = null,
  computerMode = "",
  computerSelectedPageId = null,
  onComputerActivePageIdChange = null,
}) {
  const { user, token, userPlan, login } = useAuth();
  const [step, setStep] = useState(1);
  const [websiteData, setWebsiteData] = useState({
    name: "",
    description: "",
    // Normalized pages model (future-proof for reorder + inline edits)
    pagesById: {}, // { [pageId]: { id, title, content, sections } }
    pageOrder: [], // [pageId, ...]
    standardPages: {
      about: true,
      team: true,
      careers: true,
      apps: true,
      contact: true,
      privacy: true,
      terms: true,
      cookie: true,
      refund: false, // only offered when payments are enabled
    },
    theme: "modern",
    domain: "",
    techStack: "html", // html | react
    authProviders: {
      google: false,
      microsoft: false,
      apple: false,
      facebook: false,
    },
    // Only used in "env" mode generation. IMPORTANT: these should NOT be persisted to Projects/Templates.
    authConfig: {
      redirectUrl: "", // e.g. https://yourdomain.com/auth/callback
      googleClientId: "",
      microsoftClientId: "",
      appleClientId: "",
      facebookAppId: "",
    },
    payments: {
      enabled: false,
      provider: "stripe", // stripe | none
    },
    // Only used in "env" mode generation. IMPORTANT: these should NOT be persisted to Projects/Templates.
    paymentConfig: {
      stripe: {
        publishableKey: "",
        secretKey: "",
        priceId: "",
        webhookSecret: "",
        publicUrl: "", // e.g. https://yourdomain.com
      },
    },
    integrationMode: "demo", // demo | env
    brand: {
      tone: "modern", // modern | friendly | premium | bold | minimal
      primaryColor: "#7c3aed",
      secondaryColor: "#2563eb",
      fontFamily: "system-ui",
    },
  });
  const [currentPage, setCurrentPage] = useState({
    title: "",
    content: "",
    sections: [],
  });
  const [showLogin, setShowLogin] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- Integration credentials helpers (env mode) ----
  const sanitizeWebsiteDataForPersistence = useCallback((data) => {
    const d = data && typeof data === "object" ? data : {};
    const out = { ...d };
    // Never persist credentials/secrets to backend Projects/Templates/autosave/collab.
    delete out.authConfig;
    delete out.paymentConfig;
    return out;
  }, []);

  const ensureRedirectUrl = useCallback((prev) => {
    const cur = (prev?.authConfig && typeof prev.authConfig === "object") ? prev.authConfig : {};
    const existing = String(cur.redirectUrl || "").trim();
    if (existing) return existing;
    // Default: single callback path (generated kits use this as an example)
    return `${window.location.origin}/auth/callback`;
  }, []);

  // eslint-disable-next-line no-unused-vars
  const promptAuthProviderIds = useCallback(async (provider) => {
    const p = String(provider || "").toLowerCase();
    const keyMap = {
      google: "googleClientId",
      microsoft: "microsoftClientId",
      apple: "appleClientId",
      facebook: "facebookAppId",
    };
    const field = keyMap[p];
    if (!field) return { ok: false };
    const label = p === "facebook" ? "Facebook App ID" : `${p[0].toUpperCase() + p.slice(1)} Client ID`;
    const current = String(websiteData?.authConfig?.[field] || "");
    const v = window.prompt(`${label} (required):`, current);
    if (v == null) return { ok: false, cancelled: true };
    const trimmed = String(v || "").trim();
    if (!trimmed) return { ok: false, cancelled: true };

    const currentRedirect = String(websiteData?.authConfig?.redirectUrl || "") || `${window.location.origin}/auth/callback`;
    const r = window.prompt(`Redirect URL (used by OAuth providers):`, currentRedirect);
    if (r == null) return { ok: false, cancelled: true };
    const redirectUrl = String(r || "").trim() || `${window.location.origin}/auth/callback`;

    setWebsiteData((prev) => ({
      ...prev,
      authConfig: {
        ...(prev?.authConfig && typeof prev.authConfig === "object" ? prev.authConfig : {}),
        [field]: trimmed,
        redirectUrl,
      },
    }));
    return { ok: true, field, value: trimmed, redirectUrl };
  }, [websiteData, setWebsiteData]);

  const promptStripeKeys = useCallback(async () => {
    const cur = websiteData?.paymentConfig?.stripe || {};
    const pk = window.prompt("Stripe Publishable Key (pk_...):", String(cur.publishableKey || ""));
    if (pk == null) return { ok: false, cancelled: true };
    const publishableKey = String(pk || "").trim();
    if (!publishableKey) return { ok: false, cancelled: true };

    const sk = window.prompt("Stripe Secret Key (sk_...):", String(cur.secretKey || ""));
    if (sk == null) return { ok: false, cancelled: true };
    const secretKey = String(sk || "").trim();
    if (!secretKey) return { ok: false, cancelled: true };

    const price = window.prompt("Stripe Price ID (price_...):", String(cur.priceId || ""));
    if (price == null) return { ok: false, cancelled: true };
    const priceId = String(price || "").trim();
    if (!priceId) return { ok: false, cancelled: true };

    const wh = window.prompt("Stripe Webhook Secret (whsec_...) (optional):", String(cur.webhookSecret || ""));
    if (wh == null) return { ok: false, cancelled: true };
    const webhookSecret = String(wh || "").trim();

    const defaultPublic = String(cur.publicUrl || "").trim() || window.location.origin;
    const pu = window.prompt("Public URL (for success/cancel/webhooks) (e.g. https://yourdomain.com):", defaultPublic);
    if (pu == null) return { ok: false, cancelled: true };
    const publicUrl = String(pu || "").trim() || window.location.origin;

    setWebsiteData((prev) => ({
      ...prev,
      paymentConfig: {
        ...(prev?.paymentConfig && typeof prev.paymentConfig === "object" ? prev.paymentConfig : {}),
        stripe: { publishableKey, secretKey, priceId, webhookSecret, publicUrl },
      },
    }));
    return { ok: true };
  }, [websiteData, setWebsiteData]);

  const toggleAuthProvider = useCallback(async (provider, checked) => {
    const p = String(provider || "").toLowerCase();
    setWebsiteData((prev) => ({
      ...prev,
      authProviders: { ...(prev.authProviders || {}), [p]: Boolean(checked) },
      // In env mode, ensure a default redirectUrl exists so the user sees it in the form.
      ...(String(prev?.integrationMode || "demo") === "env"
        ? { authConfig: { ...(prev.authConfig || {}), redirectUrl: ensureRedirectUrl(prev) } }
        : {}),
    }));
  }, [ensureRedirectUrl]);

  const togglePaymentsEnabled = useCallback(async (checked) => {
    setWebsiteData((prev) => ({ ...prev, payments: { ...(prev.payments || {}), enabled: Boolean(checked) } }));
  }, []);
  const stripePromiseRef = useRef(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [generatedArtifacts, setGeneratedArtifacts] = useState(null); // { html, css, js }
  const [generatedHtmlDoc, setGeneratedHtmlDoc] = useState(""); // runnable HTML string (back-compat)
  const [generatedFiles, setGeneratedFiles] = useState(null); // extra files from backend (optional)
  const [generatedInstructions, setGeneratedInstructions] = useState(""); // optional guidance from backend
  const [versions, setVersions] = useState([]); // history of generations
  const [showDeployHelp, setShowDeployHelp] = useState(false);
  const [draggingPageId, setDraggingPageId] = useState(null);
  const [showVisualEditor, setShowVisualEditor] = useState(false);
  const [visualPreview, setVisualPreview] = useState({ html: "", css: "", js: "" });
  const [activeVisualPageId, setActiveVisualPageId] = useState(null);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistError, setAiAssistError] = useState("");
  const [visualDevice, setVisualDevice] = useState("desktop"); // desktop | tablet | mobile
  const [audienceSegment, setAudienceSegment] = useState("default"); // default | new | returning | enterprise
  const [agentGoal, setAgentGoal] = useState("Make this page persuasive for sales with a clear CTA.");
  const [agentPlan, setAgentPlan] = useState(null); // { summary, operations }
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [showAgentPreview, setShowAgentPreview] = useState(true);
  const [agentPlans, setAgentPlans] = useState(null); // { A:{label,summary,operations}, B:{...} }
  const [selectedAgentPlanKey, setSelectedAgentPlanKey] = useState("A");
  const [previewSessionId, setPreviewSessionId] = useState("");
  const [kpis, setKpis] = useState({ clicks: 0, maxScrollPct: 0 });
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState("");
  const [kpiGoalLoading, setKpiGoalLoading] = useState(false);
  const [kpiGoalError, setKpiGoalError] = useState("");
  const [projectKpis, setProjectKpis] = useState(null);
  const [projectKpisLoading, setProjectKpisLoading] = useState(false);
  const [projectKpisError, setProjectKpisError] = useState("");
  const [autoOptEnabled, setAutoOptEnabled] = useState(false);
  const [autoOptIntervalMin, setAutoOptIntervalMin] = useState(60);
  const [autoOptLoading, setAutoOptLoading] = useState(false);
  const [autoOptError, setAutoOptError] = useState("");
  const [autoOptHistory, setAutoOptHistory] = useState([]);
  const [assetLibrary, setAssetLibrary] = useState([]);
  const [assetLibraryLoading, setAssetLibraryLoading] = useState(false);
  const [assetLibraryError, setAssetLibraryError] = useState("");
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceResult, setComplianceResult] = useState(null); // { errors: [], warnings: [] }
  const [complianceError, setComplianceError] = useState("");
  const [emotionLoading, setEmotionLoading] = useState(false);
  const [emotionResult, setEmotionResult] = useState(null);
  const [emotionError, setEmotionError] = useState("");
  const [heroImageLoading, setHeroImageLoading] = useState(false);
  const [heroImageError, setHeroImageError] = useState("");
  const [impactGoal, setImpactGoal] = useState("");
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState("");
  const [impactResult, setImpactResult] = useState(null);
  const [collabEnabled, setCollabEnabled] = useState(false);
  const [cloudCollabEnabled, setCloudCollabEnabled] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(() => {
    try { return localStorage.getItem("zolaid_current_project_id_v1") || ""; } catch (_) { return ""; }
  });
  const [autosaveEnabled, setAutosaveEnabled] = useState(false);
  const autosaveDebounceRef = useRef(null);
  const [computerLivePreview, setComputerLivePreview] = useState(false);
  const computerLivePreviewDebounceRef = useRef(null);
  const [projectVersions, setProjectVersions] = useState([]);
  const [projectVersionsLoading, setProjectVersionsLoading] = useState(false);
  const [projectVersionsError, setProjectVersionsError] = useState("");
  const collabClientId = useRef(`collab_${Date.now()}_${Math.random().toString(16).slice(2)}`);
  const collabChannelRef = useRef(null);
  const collabApplyingRemoteRef = useRef(false);
  const collabLastRemoteTsRef = useRef(0);
  const collabDebounceRef = useRef(null);
  const cloudEventSourceRef = useRef(null);
  const cloudApplyingRemoteRef = useRef(false);
  const cloudLastRemoteTsRef = useRef(0);
  const cloudDebounceRef = useRef(null);

  const LS_TEMPLATES_KEY = "zolaid_templates_v1";
  const LS_TEMPLATE_TO_LOAD = "zolaid_template_to_load_v1";
  const LS_PROJECT_TO_LOAD = "zolaid_project_to_load_v1";
  const LS_CURRENT_PROJECT_ID = "zolaid_current_project_id_v1";
  const COLLAB_CHANNEL_NAME = "zolaid_collab_v1";
  const LS_TEMPLATE_EDIT_KEYS = "zolaid_template_edit_keys_v1";
  const LS_LAST_PUBLISHED_TEMPLATE_ID = "zolaid_last_published_template_id_v1";

  const [lastPublishedTemplateId, setLastPublishedTemplateId] = useState(() => {
    try { return localStorage.getItem(LS_LAST_PUBLISHED_TEMPLATE_ID) || ""; } catch (_) { return ""; }
  });

  // If user opened WebsiteBuilder from chat intent, seed the description
  useEffect(() => {
    const seed = String(initialPrompt || "").trim();
    if (!seed) return;
    setStep(1);
    setWebsiteData((prev) => {
      if (prev.description && prev.description.trim()) return prev;
      return {
        ...prev,
        name: prev.name || "New Website",
        description: seed,
      };
    });
    // only when prompt changes
  }, [initialPrompt]);

  // Check if user needs to login or upgrade
  useEffect(() => {
    if (!token && step > 1) {
      setShowLogin(true);
    }
    if (userPlan === "hobby" && step >= 3) {
      setShowPayment(true);
    }
  }, [token, userPlan, step]);

  // Back-compat: if we ever have a legacy `pages: []`, normalize it once.
  useEffect(() => {
    setWebsiteData((prev) => {
      if (prev?.pagesById && prev?.pageOrder) return prev;
      if (!Array.isArray(prev?.pages)) {
        return {
          ...prev,
          pagesById: prev?.pagesById || {},
          pageOrder: prev?.pageOrder || [],
        };
      }
      const pagesById = {};
      const pageOrder = [];
      (prev.pages || []).forEach((p) => {
        const id = p?.id != null ? String(p.id) : `page_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        pagesById[id] = { ...p, id };
        pageOrder.push(id);
      });
      // keep `pages` around if present, but new code reads normalized fields
      return {
        ...prev,
        pagesById,
        pageOrder,
      };
    });
  }, []);

  // Template load handoff (from /Templates page)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_TEMPLATE_TO_LOAD);
      if (!raw) return;
      localStorage.removeItem(LS_TEMPLATE_TO_LOAD);
      const tpl = JSON.parse(raw);
      const data = tpl?.websiteData && typeof tpl.websiteData === "object" ? tpl.websiteData : null;
      if (!data) return;

      // Normalize pages if template stored legacy `pages: []`
      let pagesById = data.pagesById && typeof data.pagesById === "object" ? data.pagesById : null;
      let pageOrder = Array.isArray(data.pageOrder) ? data.pageOrder : null;
      if ((!pagesById || !pageOrder) && Array.isArray(data.pages)) {
        pagesById = {};
        pageOrder = [];
        data.pages.forEach((p, idx) => {
          const id = p?.id != null ? String(p.id) : `page_${Date.now()}_${idx}`;
          pagesById[id] = { ...p, id };
          pageOrder.push(id);
        });
      }

      const merged = {
        ...websiteData,
        ...data,
        pagesById: pagesById || websiteData.pagesById || {},
        pageOrder: pageOrder || websiteData.pageOrder || [],
      };
      // avoid leaving large legacy pages[] around
      delete merged.pages;

      setWebsiteData(merged);
      setStep(3);
      setShowVisualEditor(true);
      setTimeout(() => {
        try {
          setVisualPreview(buildVisualEditorPreview(getOrderedPages(merged), audienceSegment));
        } catch (_) {}
      }, 0);
    } catch (_) {}
    // only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Project load handoff (from /Projects page)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_PROJECT_TO_LOAD);
      if (!raw) return;
      localStorage.removeItem(LS_PROJECT_TO_LOAD);
      const project = JSON.parse(raw);
      if (project?.id) {
        setCurrentProjectId(String(project.id));
        try { localStorage.setItem(LS_CURRENT_PROJECT_ID, String(project.id)); } catch (_) {}
      }
      const data = project?.websiteData && typeof project.websiteData === "object" ? project.websiteData : null;
      if (!data) return;

      let pagesById = data.pagesById && typeof data.pagesById === "object" ? data.pagesById : null;
      let pageOrder = Array.isArray(data.pageOrder) ? data.pageOrder : null;
      if ((!pagesById || !pageOrder) && Array.isArray(data.pages)) {
        pagesById = {};
        pageOrder = [];
        data.pages.forEach((p, idx) => {
          const id = p?.id != null ? String(p.id) : `page_${Date.now()}_${idx}`;
          pagesById[id] = { ...p, id };
          pageOrder.push(id);
        });
      }

      const merged = {
        ...websiteData,
        ...data,
        pagesById: pagesById || websiteData.pagesById || {},
        pageOrder: pageOrder || websiteData.pageOrder || [],
      };
      delete merged.pages;

      setWebsiteData(merged);
      setStep(3);
      setShowVisualEditor(true);
      setTimeout(() => {
        try {
          setVisualPreview(buildVisualEditorPreview(getOrderedPages(merged), audienceSegment));
        } catch (_) {}
      }, 0);
    } catch (_) {}
    // only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProjectToBackend = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const pages = getOrderedPages(websiteData);
      const payload = { ...sanitizeWebsiteDataForPersistence(websiteData), pages };
      const isUpdate = Boolean(currentProjectId);
      const endpoint = apiUrl
        ? `${apiUrl}/projects/${isUpdate ? "update" : "create"}`
        : `/projects/${isUpdate ? "update" : "create"}`;
      const resp = await axios.post(endpoint, isUpdate
        ? { id: currentProjectId, name: websiteData?.name || "Project", websiteData: payload }
        : { name: websiteData?.name || "Project", websiteData: payload }
      );
      const id = resp.data?.project?.id;
      if (id) {
        setCurrentProjectId(String(id));
        try { localStorage.setItem(LS_CURRENT_PROJECT_ID, String(id)); } catch (_) {}
      }
      alert(isUpdate ? "Project updated (prototype)." : "Project saved (prototype). Open /Projects to load it later.");
    } catch (e) {
      console.error("save project failed:", e);
      alert(e?.response?.data?.error || "Failed to save project.");
    }
  };

  const saveAsNewProjectToBackend = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const pages = getOrderedPages(websiteData);
      const payload = { ...sanitizeWebsiteDataForPersistence(websiteData), pages };
      const endpoint = apiUrl ? `${apiUrl}/projects/create` : "/projects/create";
      const resp = await axios.post(endpoint, { name: websiteData?.name || "Project", websiteData: payload });
      const id = resp.data?.project?.id;
      if (id) {
        setCurrentProjectId(String(id));
        try { localStorage.setItem(LS_CURRENT_PROJECT_ID, String(id)); } catch (_) {}
      }
      alert("New project created (prototype).");
    } catch (e) {
      console.error("save as new project failed:", e);
      alert(e?.response?.data?.error || "Failed to save as new project.");
    }
  };

  const refreshProjectVersions = async () => {
    try {
      if (!currentProjectId) return;
      setProjectVersionsError("");
      setProjectVersionsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/projects/version/list` : "/projects/version/list";
      const resp = await axios.get(endpoint, { params: { id: currentProjectId } });
      setProjectVersions(Array.isArray(resp.data?.versions) ? resp.data.versions : []);
    } catch (e) {
      console.error("refresh project versions failed:", e);
      setProjectVersionsError(e?.response?.data?.error || "Failed to load project versions.");
    } finally {
      setProjectVersionsLoading(false);
    }
  };

  const saveProjectVersion = async () => {
    try {
      if (!currentProjectId) return;
      const label = prompt("Snapshot name:", `Snapshot ${new Date().toLocaleString()}`) || "";
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/projects/version/save` : "/projects/version/save";
      await axios.post(endpoint, { id: currentProjectId, label });
      await refreshProjectVersions();
      alert("Snapshot saved.");
    } catch (e) {
      console.error("save project version failed:", e);
      alert(e?.response?.data?.error || "Failed to save snapshot.");
    }
  };

  const restoreProjectVersion = async (versionId) => {
    try {
      if (!currentProjectId) return;
      const ok = window.confirm("Restore this snapshot? This will overwrite the current project state.");
      if (!ok) return;
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/projects/version/restore` : "/projects/version/restore";
      await axios.post(endpoint, { id: currentProjectId, versionId });
      // Fetch fresh project and load it into builder state
      const getEndpoint = apiUrl ? `${apiUrl}/projects/get` : "/projects/get";
      const resp = await axios.get(getEndpoint, { params: { id: currentProjectId } });
      const project = resp.data?.project;
      const data = project?.websiteData;
      if (data && typeof data === "object") {
        let pagesById = data.pagesById && typeof data.pagesById === "object" ? data.pagesById : null;
        let pageOrder = Array.isArray(data.pageOrder) ? data.pageOrder : null;
        if ((!pagesById || !pageOrder) && Array.isArray(data.pages)) {
          pagesById = {};
          pageOrder = [];
          data.pages.forEach((p, idx) => {
            const id = p?.id != null ? String(p.id) : `page_${Date.now()}_${idx}`;
            pagesById[id] = { ...p, id };
            pageOrder.push(id);
          });
        }
        const merged = { ...websiteData, ...data, pagesById: pagesById || websiteData.pagesById || {}, pageOrder: pageOrder || websiteData.pageOrder || [] };
        delete merged.pages;
        setWebsiteData(merged);
        setTimeout(() => {
          try { setVisualPreview(buildVisualEditorPreview(getOrderedPages(merged), audienceSegment)); } catch (_) {}
        }, 0);
      }
      await refreshProjectVersions();
    } catch (e) {
      console.error("restore project version failed:", e);
      alert(e?.response?.data?.error || "Failed to restore snapshot.");
    }
  };

  useEffect(() => {
    if (!autosaveEnabled) return;
    if (!currentProjectId) return;
    // debounce autosave on edits
    if (autosaveDebounceRef.current) clearTimeout(autosaveDebounceRef.current);
    autosaveDebounceRef.current = setTimeout(() => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const pages = getOrderedPages(websiteData);
        const payload = { ...sanitizeWebsiteDataForPersistence(websiteData), pages };
        const endpoint = apiUrl ? `${apiUrl}/projects/update` : "/projects/update";
        axios.post(endpoint, { id: currentProjectId, name: websiteData?.name || "Project", websiteData: payload }).catch(() => {});
      } catch (_) {}
    }, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosaveEnabled, currentProjectId, websiteData]);

  const getCollabPayload = (data) => {
    // Keep it small and deterministic (exclude generated artifacts/versions)
    return {
      name: data?.name || "",
      description: data?.description || "",
      domain: data?.domain || "",
      theme: data?.theme || "modern",
      techStack: data?.techStack || "html",
      authProviders: data?.authProviders || {},
      payments: data?.payments || { enabled: false, provider: "stripe" },
      integrationMode: data?.integrationMode || "demo",
      standardPages: data?.standardPages || {},
      pagesById: data?.pagesById || {},
      pageOrder: data?.pageOrder || [],
    };
  };

  // Collaboration (same-browser tabs) via BroadcastChannel
  useEffect(() => {
    if (!collabEnabled) {
      try {
        if (collabChannelRef.current) collabChannelRef.current.close();
      } catch (_) {}
      collabChannelRef.current = null;
      return;
    }

    let ch = null;
    try {
      ch = new BroadcastChannel(COLLAB_CHANNEL_NAME);
    } catch (e) {
      console.error("BroadcastChannel not available:", e);
      setCollabEnabled(false);
      return;
    }
    collabChannelRef.current = ch;

    const handler = (event) => {
      const msg = event?.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type !== "sync") return;
      if (msg.clientId === collabClientId.current) return;
      const ts = Number(msg.ts || 0);
      if (!Number.isFinite(ts) || ts <= collabLastRemoteTsRef.current) return;
      collabLastRemoteTsRef.current = ts;
      const payload = msg.payload;
      if (!payload || typeof payload !== "object") return;

      collabApplyingRemoteRef.current = true;
      setWebsiteData((prev) => ({
        ...prev,
        ...payload,
        // Keep any local transient fields that arenâ€™t in payload
      }));
      setTimeout(() => {
        collabApplyingRemoteRef.current = false;
      }, 0);
    };

    ch.addEventListener("message", handler);

    // Initial announce
    try {
      ch.postMessage({
        type: "sync",
        clientId: collabClientId.current,
        ts: Date.now(),
        payload: getCollabPayload(websiteData),
      });
    } catch (_) {}

    return () => {
      try {
        ch.removeEventListener("message", handler);
        ch.close();
      } catch (_) {}
      collabChannelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collabEnabled]);

  useEffect(() => {
    if (!collabEnabled) return;
    if (!collabChannelRef.current) return;
    if (collabApplyingRemoteRef.current) return;

    // Debounce broadcasts
    if (collabDebounceRef.current) clearTimeout(collabDebounceRef.current);
    collabDebounceRef.current = setTimeout(() => {
      try {
        collabChannelRef.current.postMessage({
          type: "sync",
          clientId: collabClientId.current,
          ts: Date.now(),
          payload: getCollabPayload(websiteData),
        });
      } catch (_) {}
    }, 200);
  }, [collabEnabled, websiteData]);

  // Cloud collaboration (cross-device) via SSE + broadcast
  useEffect(() => {
    if (!cloudCollabEnabled || !currentProjectId) {
      try { if (cloudEventSourceRef.current) cloudEventSourceRef.current.close(); } catch (_) {}
      cloudEventSourceRef.current = null;
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const base = apiUrl ? apiUrl.replace(/\/+$/, "") : "";
      const url = `${base}/collab/stream?projectId=${encodeURIComponent(currentProjectId)}`;
      const es = new EventSource(url);
      cloudEventSourceRef.current = es;

      es.addEventListener("message", (evt) => {
        try {
          const msg = JSON.parse(evt.data || "{}");
          if (msg.clientId === collabClientId.current) return;
          const ts = Number(msg.ts || 0);
          if (!Number.isFinite(ts) || ts <= cloudLastRemoteTsRef.current) return;
          cloudLastRemoteTsRef.current = ts;
          const payload = msg.payload;
          if (!payload || typeof payload !== "object") return;
          cloudApplyingRemoteRef.current = true;
          setWebsiteData((prev) => ({ ...prev, ...payload }));
          setTimeout(() => { cloudApplyingRemoteRef.current = false; }, 0);
        } catch (_) {}
      });

      es.addEventListener("error", () => {
        // keep enabled; SSE will retry
      });
    } catch (e) {
      console.error("Cloud collab setup failed:", e);
      setCloudCollabEnabled(false);
    }

    return () => {
      try { if (cloudEventSourceRef.current) cloudEventSourceRef.current.close(); } catch (_) {}
      cloudEventSourceRef.current = null;
    };
  }, [cloudCollabEnabled, currentProjectId]);

  useEffect(() => {
    if (!cloudCollabEnabled || !currentProjectId) return;
    if (cloudApplyingRemoteRef.current) return;

    if (cloudDebounceRef.current) clearTimeout(cloudDebounceRef.current);
    cloudDebounceRef.current = setTimeout(() => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const endpoint = apiUrl ? `${apiUrl.replace(/\/+$/, "")}/collab/broadcast` : "/collab/broadcast";
        axios.post(endpoint, {
          projectId: currentProjectId,
          clientId: collabClientId.current,
          ts: Date.now(),
          payload: getCollabPayload(websiteData),
        }).catch(() => {});
      } catch (_) {}
    }, 250);
  }, [cloudCollabEnabled, currentProjectId, websiteData]);

  const saveTemplate = () => {
    try {
      const pages = getOrderedPages(websiteData);
      const payload = {
        name: websiteData?.name || "Website",
        websiteData: {
          ...websiteData,
          pages,
        },
      };
      const templates = (() => {
        try {
          const raw = localStorage.getItem(LS_TEMPLATES_KEY);
          const parsed = raw ? JSON.parse(raw) : [];
          return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return [];
        }
      })();
      templates.unshift({
        id: `tpl_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name: payload.name,
        createdAt: Date.now(),
        websiteData: payload.websiteData,
      });
      localStorage.setItem(LS_TEMPLATES_KEY, JSON.stringify(templates.slice(0, 50)));
      alert("Template saved (prototype). Open /Templates to manage it.");
    } catch (e) {
      console.error("Save template failed:", e);
      alert("Failed to save template.");
    }
  };

  const getEditKeyForTemplateId = (id) => {
    try {
      const raw = localStorage.getItem(LS_TEMPLATE_EDIT_KEYS);
      const parsed = raw ? JSON.parse(raw) : {};
      const map = parsed && typeof parsed === "object" ? parsed : {};
      return id ? String(map[String(id)] || "") : "";
    } catch (_) {
      return "";
    }
  };

  const setEditKeyForTemplateId = (id, editKey) => {
    try {
      const raw = localStorage.getItem(LS_TEMPLATE_EDIT_KEYS);
      const parsed = raw ? JSON.parse(raw) : {};
      const map = parsed && typeof parsed === "object" ? parsed : {};
      map[String(id)] = String(editKey);
      localStorage.setItem(LS_TEMPLATE_EDIT_KEYS, JSON.stringify(map));
    } catch (_) {}
  };

  const publishCommunityTemplate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/market/templates/publish` : "/market/templates/publish";

      const pages = getOrderedPages(websiteData).map((p) => {
        const mat = materializeFromSections(p.sections, "default");
        return { ...p, contentHtml: mat.html, content: mat.text, sections: p.sections };
      });
      const websitePayload = { ...sanitizeWebsiteDataForPersistence(websiteData), pages };

      const name = window.prompt("Community template name:", String(websiteData?.name || "Template")) || String(websiteData?.name || "Template");
      const description = window.prompt("Template description (optional):", "") || "";
      const tagsRaw = window.prompt("Tags (optional, comma-separated):", "") || "";
      const author =
        window.prompt(
          "Author display name (optional):",
          (() => {
            try { return localStorage.getItem("zolaid_template_author") || ""; } catch (_) { return ""; }
          })()
        ) || "";
      try { localStorage.setItem("zolaid_template_author", author); } catch (_) {}
      const tags = tagsRaw.split(",").map((t) => String(t).trim()).filter(Boolean).slice(0, 10);

      const resp = await axios.post(endpoint, { template: { name, description, tags, author, websiteData: websitePayload } });
      const id = resp.data?.id;
      const editKey = resp.data?.editKey;
      if (id && editKey) setEditKeyForTemplateId(id, editKey);
      if (id) {
        setLastPublishedTemplateId(String(id));
        try { localStorage.setItem(LS_LAST_PUBLISHED_TEMPLATE_ID, String(id)); } catch (_) {}
        try { await navigator.clipboard.writeText(String(id)); } catch (_) {}
        const msg = `Published.\nShare ID: ${id}\nShare link: /Templates?view=${id}\neditKey: ${editKey ? String(editKey) : "(missing)"}`;
        alert(msg);
        if (editKey) {
          try { await navigator.clipboard.writeText(String(editKey)); } catch (_) {}
        }
      }
    } catch (e) {
      console.error("publish community template failed:", e);
      alert(e?.response?.data?.error || "Failed to publish community template.");
    }
  };

  const updatePublishedCommunityTemplate = async () => {
    try {
      const id = window.prompt("Template Share ID to update:", String(lastPublishedTemplateId || "")) || "";
      const templateId = String(id).trim();
      if (!templateId) return;
      const editKey = getEditKeyForTemplateId(templateId);
      if (!editKey) {
        alert("No edit key found for this template on this browser.");
        return;
      }
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/market/templates/update` : "/market/templates/update";

      const pages = getOrderedPages(websiteData).map((p) => {
        const mat = materializeFromSections(p.sections, "default");
        return { ...p, contentHtml: mat.html, content: mat.text, sections: p.sections };
      });
      const websitePayload = { ...sanitizeWebsiteDataForPersistence(websiteData), pages };

      const name = window.prompt("Update template name:", String(websiteData?.name || "Template")) || String(websiteData?.name || "Template");
      const description = window.prompt("Update description (optional):", "") || "";
      const tagsRaw = window.prompt("Update tags (optional, comma-separated):", "") || "";
      const author =
        window.prompt(
          "Author display name (optional):",
          (() => {
            try { return localStorage.getItem("zolaid_template_author") || ""; } catch (_) { return ""; }
          })()
        ) || "";
      try { localStorage.setItem("zolaid_template_author", author); } catch (_) {}
      const tags = tagsRaw.split(",").map((t) => String(t).trim()).filter(Boolean).slice(0, 10);

      await axios.post(endpoint, { id: templateId, editKey, patch: { name, description, tags, author, websiteData: websitePayload } });
      setLastPublishedTemplateId(String(templateId));
      try { localStorage.setItem(LS_LAST_PUBLISHED_TEMPLATE_ID, String(templateId)); } catch (_) {}
      alert("Template updated.");
    } catch (e) {
      console.error("update community template failed:", e);
      alert(e?.response?.data?.error || "Failed to update community template.");
    }
  };

  const copyPublishedTemplateShareLink = async () => {
    try {
      const id = String(lastPublishedTemplateId || "").trim();
      if (!id) return;
      const link = `${window.location.origin}/Templates?view=${encodeURIComponent(id)}`;
      try {
        await navigator.clipboard.writeText(String(link));
        alert("Share link copied.");
      } catch (_) {
        window.prompt("Copy this share link:", link);
      }
    } catch (_) {}
  };

  const openPublishedTemplateInTemplates = () => {
    try {
      const id = String(lastPublishedTemplateId || "").trim();
      if (!id) return;
      window.location.assign(`/Templates?view=${encodeURIComponent(id)}`);
    } catch (_) {}
  };

  const deletePublishedCommunityTemplate = async () => {
    try {
      const id = window.prompt("Template Share ID to delete:", String(lastPublishedTemplateId || "")) || "";
      const templateId = String(id).trim();
      if (!templateId) return;
      const editKey = getEditKeyForTemplateId(templateId);
      if (!editKey) {
        alert("No edit key found for this template on this browser.");
        return;
      }
      const ok = window.confirm("Delete this published community template? This cannot be undone.");
      if (!ok) return;
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/market/templates/delete-own` : "/market/templates/delete-own";
      await axios.post(endpoint, { id: templateId, editKey });
      alert("Published template deleted.");
      if (String(lastPublishedTemplateId || "") === String(templateId)) {
        setLastPublishedTemplateId("");
        try { localStorage.removeItem(LS_LAST_PUBLISHED_TEMPLATE_ID); } catch (_) {}
      }
    } catch (e) {
      console.error("delete community template failed:", e);
      alert(e?.response?.data?.error || "Failed to delete published template.");
    }
  };

  const copyBuilderLinkForCurrentProject = async () => {
    try {
      if (!currentProjectId) {
        alert("No current project id. Save your project first.");
        return;
      }
      const origin = window.location.origin || "";
      const url = `${origin}/chatbot?projectId=${encodeURIComponent(String(currentProjectId))}`;
      try {
        await navigator.clipboard.writeText(url);
        alert("Builder link copied.");
      } catch (_) {
        window.prompt("Copy builder link:", url);
      }
    } catch (_) {}
  };

  const openBuilderLinkForCurrentProject = () => {
    try {
      if (!currentProjectId) {
        alert("No current project id. Save your project first.");
        return;
      }
      const origin = window.location.origin || "";
      const url = `${origin}/chatbot?projectId=${encodeURIComponent(String(currentProjectId))}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {}
  };

  const sendCurrentToComputerPreview = useCallback(() => {
    try {
      if (typeof onSendToComputerPreview !== "function") return;
      const pages = getOrderedPages(websiteData).filter((p) => p && p.visible !== false);
      if (!pages.length) {
        alert("No pages to preview.");
        return;
      }
      const activeId = activeVisualPageId || pages[0]?.id;
      const navLinks = pages
        .map(
          (p) =>
            `<a href="#${String(p.id)}" data-nav="${String(p.id)}" style="text-decoration:none;padding:8px 10px;border-radius:10px;background:rgba(0,0,0,0.08);color:inherit;">${String(
              p.title || p.id
            )}</a>`
        )
        .join("");
      const sectionsHtml = pages
        .map((p) => {
          const mat = materializeFromSections(p.sections, "default");
          const display = String(p.id) === String(activeId) ? "block" : "none";
          return `<section id="${String(p.id)}" data-page="${String(p.id)}" style="display:${display};max-width:980px;margin:0 auto;padding:16px 0;">
            <h1 style="margin:0 0 12px 0;">${String(p.title || "Untitled")}</h1>
            <div>${mat.html}</div>
          </section>`;
        })
        .join("\n");

      const footerLinks = pages
        .map(
          (p) =>
            `<a href="#${String(p.id)}" data-nav="${String(p.id)}" style="text-decoration:none;color:inherit;opacity:0.85;font-size:13px;">${String(
              p.title || p.id
            )}</a>`
        )
        .join(" &middot; ");
      const html = `
        <div style="max-width:1100px;margin:0 auto;min-height:100vh;display:flex;flex-direction:column;">
          <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;position:sticky;top:0;background:rgba(255,255,255,0.92);backdrop-filter:blur(8px);padding:10px 0;border-bottom:1px solid rgba(0,0,0,0.08);">
            ${navLinks}
          </div>
          <div style="flex:1;">
            ${sectionsHtml}
          </div>
          <footer style="margin-top:auto;padding:20px 0;border-top:1px solid rgba(0,0,0,0.08);text-align:center;font-size:13px;color:#6b7280;">
            ${footerLinks || "â€”"}
          </footer>
        </div>
      `;
      const css = `
        body { font-family: ${websiteData?.brand?.fontFamily || "system-ui"}; margin: 0; padding: 18px; color: #111827; background: #ffffff; }
        h1,h2,h3,h4,h5,h6 { color: ${websiteData?.brand?.primaryColor || "#7c3aed"}; }
        a { color: ${websiteData?.brand?.secondaryColor || "#2563eb"}; }
        img, video { max-width: 100%; height: auto; border-radius: 12px; }
        p { line-height: 1.6; }
      `;
      const js = `
        (function(){
          function sendSelect(id){
            try {
              parent.postMessage({ __zolaidPreview: true, payload: { type: 'editor', action: 'select_page', pageId: id, origin: 'preview', ts: Date.now() } }, '*');
            } catch (_) {}
          }
          var lastSetHash = null;
          function show(id, silent){
            document.querySelectorAll('[data-page]').forEach(function(s){
              s.style.display = (s.getAttribute('data-page') === id) ? 'block' : 'none';
            });
            try {
              if (silent) {
                // keep URL in sync without triggering hashchange/back-forward noise
                history.replaceState(null, '', '#' + String(id));
              } else {
                lastSetHash = String(id);
                if (String(location.hash || '').replace('#','') !== String(id)) location.hash = '#' + String(id);
              }
            } catch (_) {}
            if (!silent) sendSelect(id);
          }
          window.addEventListener('hashchange', function(){
            try {
              var id = (location.hash || '').replace('#','');
              if (!id) return;
              if (lastSetHash && String(lastSetHash) === String(id)) {
                lastSetHash = null;
                return;
              }
              // back/forward should update UI and sync host
              show(String(id), false);
            } catch (_) {}
          }, true);
          // host -> iframe navigation
          window.addEventListener('message', function(ev){
            try {
              var d = ev && ev.data ? ev.data : null;
              if (!d || d.__zolaidHost !== true) return;
              var p = d && d.payload ? d.payload : null;
              if (!p || String(p.type) !== 'select_page') return;
              var id = p.pageId;
              if (id) show(String(id), true);
            } catch (_) {}
          }, true);
          document.addEventListener('click', function(e){
            var a = e.target && e.target.closest ? e.target.closest('a[data-nav]') : null;
            if(!a) return;
            e.preventDefault();
            var id = a.getAttribute('data-nav');
            if(id) show(id, false);
          }, true);
          // hash navigation
          try {
            var h = (location.hash || '').replace('#','');
            if (h) show(h, false);
          } catch (_) {}
          // initial selection
          try {
            var first = (location.hash || '').replace('#','') || ${JSON.stringify(String(activeVisualPageId || ""))};
            if (first) sendSelect(first);
          } catch (_) {}
        })();
      `;
      onSendToComputerPreview({ html, css, js });
    } catch (_) {}
  }, [activeVisualPageId, onSendToComputerPreview, websiteData]);

  // Preview -> Builder page selection (when embedded in Zolaid Computer)
  useEffect(() => {
    try {
      if (!computerSelectedPageId) return;
      const next = String(computerSelectedPageId);
      if (!next) return;
      if (String(activeVisualPageId || "") === next) return;
      setActiveVisualPageId(next);
    } catch (_) {}
  }, [computerSelectedPageId, activeVisualPageId]);

  // Builder -> Computer page selection (so Zolaid Computer can drive Preview without reload)
  useEffect(() => {
    try {
      if (typeof onComputerActivePageIdChange !== "function") return;
      if (!activeVisualPageId) return;
      onComputerActivePageIdChange(String(activeVisualPageId));
    } catch (_) {}
  }, [activeVisualPageId, onComputerActivePageIdChange]);

  // If we're embedded in ZolaidComputer split mode, default to live preview ON.
  useEffect(() => {
    try {
      if (typeof onSendToComputerPreview !== "function") return;
      if (String(computerMode) !== "builderSplit") return;
      setComputerLivePreview(true);
    } catch (_) {}
    // only when embedding mode changes
  }, [computerMode, onSendToComputerPreview]);

  // Live preview into ZolaidComputer (debounced)
  useEffect(() => {
    if (!computerLivePreview) return;
    if (typeof onSendToComputerPreview !== "function") return;
    // Only auto-send when user is actively editing pages (Step 3).
    if (step !== 3) return;

    try {
      if (computerLivePreviewDebounceRef.current) {
        clearTimeout(computerLivePreviewDebounceRef.current);
      }
      computerLivePreviewDebounceRef.current = setTimeout(() => {
        sendCurrentToComputerPreview();
      }, 650);
    } catch (_) {}

    return () => {
      try {
        if (computerLivePreviewDebounceRef.current) {
          clearTimeout(computerLivePreviewDebounceRef.current);
          computerLivePreviewDebounceRef.current = null;
        }
      } catch (_) {}
    };
  }, [computerLivePreview, onSendToComputerPreview, step, websiteData, activeVisualPageId]);

  // Keyboard shortcuts (Step 3)
  // - Ctrl/Cmd+Enter: send snapshot to preview
  // - Ctrl/Cmd+Shift+Enter: toggle live preview
  useEffect(() => {
    if (step !== 3) return;
    const onKeyDown = (e) => {
      try {
        if (!e) return;
        const isMod = e.ctrlKey || e.metaKey;
        if (!isMod) return;
        if (e.key !== "Enter") return;
        e.preventDefault();
        if (e.shiftKey) {
          if (typeof onSendToComputerPreview !== "function") return;
          setComputerLivePreview((v) => !v);
          return;
        }
        sendCurrentToComputerPreview();
      } catch (_) {}
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [step, onSendToComputerPreview, sendCurrentToComputerPreview]);

  const makePageId = () => `page_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const getOrderedPages = (data) => {
    if (!data) return [];
    const pagesById = data.pagesById || {};
    const pageOrder = Array.isArray(data.pageOrder) ? data.pageOrder : [];
    const ordered = pageOrder.map((id) => pagesById[id]).filter(Boolean);
    // If nothing is in the normalized model but legacy pages exist, fall back gracefully.
    if (ordered.length === 0 && Array.isArray(data.pages)) return data.pages;
    return ordered;
  };

  const addPage = (page) => {
    const id = String(page?.id ?? makePageId());
    const safePage = {
      id,
      title: String(page?.title || ""),
      content: String(page?.content || ""),
      contentHtml: typeof page?.contentHtml === "string" ? page.contentHtml : "",
      sections: Array.isArray(page?.sections) ? page.sections : [],
      visible: page?.visible == null ? true : Boolean(page.visible),
      versions: Array.isArray(page?.versions) ? page.versions : [],
    };
    setWebsiteData((prev) => ({
      ...prev,
      pagesById: { ...(prev.pagesById || {}), [id]: safePage },
      pageOrder: [...(prev.pageOrder || []), id],
    }));
    return id;
  };

  const updatePage = (pageId, patch) => {
    const id = String(pageId || "");
    if (!id) return;
    setWebsiteData((prev) => {
      const existing = prev.pagesById?.[id];
      if (!existing) return prev;
      return {
        ...prev,
        pagesById: {
          ...(prev.pagesById || {}),
          [id]: { ...existing, ...(patch || {}), id },
        },
      };
    });
  };

  const removePage = (pageId) => {
    const id = String(pageId || "");
    if (!id) return;
    setWebsiteData((prev) => {
      const nextById = { ...(prev.pagesById || {}) };
      delete nextById[id];
      return {
        ...prev,
        pagesById: nextById,
        pageOrder: (prev.pageOrder || []).filter((x) => x !== id),
      };
    });
  };

  const reorderPages = (activeId, overId) => {
    const a = String(activeId || "");
    const b = String(overId || "");
    if (!a || !b || a === b) return;
    setWebsiteData((prev) => {
      const order = [...(prev.pageOrder || [])];
      const from = order.indexOf(a);
      const to = order.indexOf(b);
      if (from === -1 || to === -1) return prev;
      order.splice(from, 1);
      order.splice(to, 0, a);
      return { ...prev, pageOrder: order };
    });
  };

  const validation = useMemo(() => {
    const pages = getOrderedPages(websiteData);
    const errors = [];
    const warnings = [];

    const emptyTitles = pages.filter((p) => !String(p?.title || "").trim());
    if (emptyTitles.length > 0) errors.push("Every page must have a title.");
    if (pages.length === 0) errors.push("Add at least one page before generating.");

    const paymentsEnabled = Boolean(websiteData?.payments?.enabled);
    if (paymentsEnabled) {
      const hasPaymentSection = pages.some((p) =>
        Array.isArray(p?.sections) && p.sections.some((s) => String(s?.type || "").toLowerCase() === "payment")
      );
      const hasPricingLikePage = pages.some((p) => {
        const t = String(p?.title || "").toLowerCase();
        return t.includes("pricing") || t.includes("checkout") || t.includes("payment");
      });
      if (!hasPaymentSection && !hasPricingLikePage) {
        warnings.push("Payments are enabled: consider adding a Pricing/Checkout page or a Payment section.");
      }
      const hasRefundPage = pages.some((p) => String(p?.title || "").toLowerCase().includes("refund"));
      if (!hasRefundPage) {
        warnings.push("Payments are enabled: consider adding a Refund Policy page.");
      }
    }

    // Env mode: require provider IDs/keys when integrations are enabled
    const mode = String(websiteData?.integrationMode || "demo");
    if (mode === "env") {
      const ap = websiteData?.authProviders || {};
      const anyAuth = Boolean(ap.google || ap.microsoft || ap.apple || ap.facebook);
      const cfg = websiteData?.authConfig || {};
      const redirect = String(cfg.redirectUrl || "").trim();
      if (anyAuth && !redirect) errors.push("Login is enabled (env mode): Redirect URL is required.");
      if (ap.google && !String(cfg.googleClientId || "").trim()) errors.push("Google login (env mode): Client ID is required.");
      if (ap.microsoft && !String(cfg.microsoftClientId || "").trim()) errors.push("Microsoft login (env mode): Client ID is required.");
      if (ap.apple && !String(cfg.appleClientId || "").trim()) errors.push("Apple login (env mode): Client ID is required.");
      if (ap.facebook && !String(cfg.facebookAppId || "").trim()) errors.push("Facebook login (env mode): App ID is required.");

      const pay = websiteData?.payments || {};
      const provider = String(pay.provider || "stripe").toLowerCase();
      if (Boolean(pay.enabled) && provider === "stripe") {
        const s = websiteData?.paymentConfig?.stripe || {};
        if (!String(s.publishableKey || "").trim()) errors.push("Payments (Stripe env mode): Publishable key is required.");
        if (!String(s.secretKey || "").trim()) errors.push("Payments (Stripe env mode): Secret key is required.");
        if (!String(s.priceId || "").trim()) errors.push("Payments (Stripe env mode): Price ID is required.");
        if (!String(s.publicUrl || "").trim()) errors.push("Payments (Stripe env mode): Public URL is required.");
      }
    }

    return { errors, warnings };
  }, [websiteData]);

  const handleGoogleCredential = async (credentialResponse) => {
    try {
      setLoading(true);
      const cred = credentialResponse?.credential;
      if (!cred) {
        setError("Google login failed: missing credential.");
        return;
      }
      const base = getApiBase();
      const endpoint = base ? `${base}/auth/google` : "/auth/google";
      const resp = await axios.post(endpoint, { token: cred });
      if (resp?.data?.token) {
        login(resp.data.token, resp.data.user);
        setShowLogin(false);
        setError(null);
      } else {
        setError(resp?.data?.message || "Google login failed.");
      }
    } catch (err) {
      console.error("Google login failed", err);
      setError(err?.response?.data?.message || "Google login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Back-compat: old button handler (kept to avoid stale references)
  const handleGoogleLogin = async () => {
    setError("Google login needs the embedded Google button. Please click the Google sign-in widget.");
  };

  const handleStripeCheckout = async (plan = "pro") => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const endpoint = apiUrl ? `${apiUrl}/create-checkout-session` : '/create-checkout-session';
      const response = await axios.post(endpoint, {
        priceId: plan === "pro" ? process.env.REACT_APP_PRICE_PRO_MONTHLY : process.env.REACT_APP_PRICE_ULT_MONTHLY,
        userEmail: user?.email,
        userId: user?.id,
      });

      const pk = String(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "").trim();
      if (!pk) {
        alert("Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY (publishable key).");
        return;
      }
      if (!stripePromiseRef.current) {
        const { loadStripe } = await import("@stripe/stripe-js");
        stripePromiseRef.current = loadStripe(pk);
      }
      let stripe = null;
      try {
        stripe = await stripePromiseRef.current;
      } catch (e) {
        console.error("Failed to load Stripe.js", e);
        alert(
          "Failed to load Stripe.js. This is commonly caused by an ad blocker or network/CSP blocking js.stripe.com. Disable blockers for localhost or allow js.stripe.com and try again."
        );
        return;
      }
      if (!stripe) {
        alert(
          "Failed to initialize Stripe. Please check REACT_APP_STRIPE_PUBLISHABLE_KEY and whether js.stripe.com is reachable."
        );
        return;
      }
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.id,
      });

      if (error) {
        setError(error.message);
      }
    } catch (err) {
      console.error("Checkout error", err);
      setError("Payment setup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: Date.now(),
      type,
      content: "",
      config: {},
    };
    setCurrentPage({
      ...currentPage,
      sections: [...currentPage.sections, newSection],
    });
  };

  const buildStandardBusinessPages = ({ standardPages, paymentsEnabled }) => {
    const selected = standardPages || {};
    const safeSelected = {
      ...selected,
      refund: paymentsEnabled ? Boolean(selected.refund) : false,
    };

    const pages = [];
    if (safeSelected.about) {
      pages.push({
        standardKey: "about",
        title: "About Us",
        content:
          "Zolaid is an AI agent for general assistanceâ€”helping you get things done with intelligence and ease.",
        contentHtml:
          "<p>Zolaid is an AI agent for general assistanceâ€”helping you get things done with intelligence and ease.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.team) {
      pages.push({
        standardKey: "team",
        title: "Team",
        content:
          "At Zolaid, we value team members who take initiative, tackle challenges creatively, inspire through their expertise, and thrive in collaboration. Together, we drive innovation and deliver solutions that make a real impact.",
        contentHtml:
          "<p>At Zolaid, we value team members who take initiative, tackle challenges creatively, inspire through their expertise, and thrive in collaboration. Together, we drive innovation and deliver solutions that make a real impact.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.careers) {
      pages.push({
        standardKey: "careers",
        title: "Careers",
        content:
          "At Zolaid, we value team members who take initiative, tackle challenges creatively, inspire through their expertise, and thrive in collaboration. Together, we drive innovation and deliver solutions that make a real impact.",
        contentHtml:
          "<p>At Zolaid, we value team members who take initiative, tackle challenges creatively, inspire through their expertise, and thrive in collaboration. Together, we drive innovation and deliver solutions that make a real impact.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.apps) {
      pages.push({
        standardKey: "apps",
        title: "Apps",
        content:
          "Zolaid connects with the apps you already use. Creative: Photoshop, Illustrator, Canva, Figma, Sketch, Procreate. Music & audio: Spotify, Apple Music, YouTube Music, SoundCloud. Travel: TripAdvisor, Booking.com, Airbnb, Google Maps, Expedia, Kayak. Productivity: Google Workspace, Microsoft 365, Notion, Slack, Trello, Asana, Monday.com. Social: Instagram, Facebook, X (Twitter), LinkedIn, TikTok, Pinterest. Communication: Gmail, Outlook, Zoom, Microsoft Teams, Discord. Development: Lovable, Vercel, GitHub. Payments: Stripe, PayPal. And moreâ€”we keep adding integrations so you can get more done in one place.",
        contentHtml:
          "<p>Zolaid connects with the apps you already use. One assistant, many integrations.</p>" +
          "<p><strong>Creative & design:</strong> Photoshop, Illustrator, Canva, Figma, Sketch, Procreate</p>" +
          "<p><strong>Music & audio:</strong> Spotify, Apple Music, YouTube Music, SoundCloud</p>" +
          "<p><strong>Travel:</strong> TripAdvisor, Booking.com, Airbnb, Google Maps, Expedia, Kayak</p>" +
          "<p><strong>Productivity:</strong> Google Workspace, Microsoft 365, Notion, Slack, Trello, Asana, Monday.com</p>" +
          "<p><strong>Social:</strong> Instagram, Facebook, X (Twitter), LinkedIn, TikTok, Pinterest</p>" +
          "<p><strong>Communication:</strong> Gmail, Outlook, Zoom, Microsoft Teams, Discord</p>" +
          "<p><strong>Development & no-code:</strong> Lovable, Vercel, GitHub</p>" +
          "<p><strong>Payments:</strong> Stripe, PayPal</p>" +
          "<p>We keep adding new connections so you can get more done without switching context.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.contact) {
      pages.push({
        standardKey: "contact",
        title: "Contact Us",
        content:
          "Provide ways for customers to reach you: email, phone, address, hours, and a contact form.",
        contentHtml:
          "<p>Provide ways for customers to reach you: email, phone, address, hours, and a contact form.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.privacy) {
      pages.push({
        standardKey: "privacy",
        title: "Privacy Policy",
        content:
          "Explain what data you collect, why you collect it, how you use it, and how users can contact you with privacy questions.",
        contentHtml:
          "<p>Explain what data you collect, why you collect it, how you use it, and how users can contact you with privacy questions.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.terms) {
      pages.push({
        standardKey: "terms",
        title: "Terms of Service",
        content:
          "Define the rules for using your website/service: acceptable use, disclaimers, limitation of liability, and termination.",
        contentHtml:
          "<p>Define the rules for using your website/service: acceptable use, disclaimers, limitation of liability, and termination.</p>",
        sections: [],
        visible: true,
      });
    }
    if (safeSelected.cookie) {
      pages.push({
        standardKey: "cookie",
        title: "Cookie Policy",
        content:
          "Describe what cookies you use, why, and how users can manage cookie preferences in their browser.",
        contentHtml:
          "<p>Describe what cookies you use, why, and how users can manage cookie preferences in their browser.</p>",
        sections: [],
        visible: true,
      });
    }
    if (paymentsEnabled && safeSelected.refund) {
      pages.push({
        standardKey: "refund",
        title: "Refund Policy",
        content:
          "Describe your refund terms: eligibility, timelines, how to request a refund, and any exclusions for digital goods or services.",
        contentHtml:
          "<p>Describe your refund terms: eligibility, timelines, how to request a refund, and any exclusions for digital goods or services.</p>",
        sections: [],
        visible: true,
      });
    }
    return pages;
  };

  const buildVisualEditorPreview = (pages, segmentKey = "default") => {
    const safePages = Array.isArray(pages) ? pages : [];
    const initialPages = safePages
      .map((p) => ({
        id: String(p?.id || ""),
        title: String(p?.title || ""),
        content: String(p?.content || ""),
        contentHtml: typeof p?.contentHtml === "string" ? p.contentHtml : "",
        sections: Array.isArray(p?.sections) ? p.sections : [],
        visible: p?.visible == null ? true : Boolean(p.visible),
      }))
      .filter((p) => p.id);

    const css = `
      :root {
        --bg: #0b1020;
        --panel: rgba(255,255,255,0.08);
        --panel2: rgba(255,255,255,0.06);
        --border: rgba(255,255,255,0.14);
        --text: rgba(255,255,255,0.92);
        --muted: rgba(255,255,255,0.72);
        --accent: #7c3aed;
      }
      body { background: var(--bg); color: var(--text); }
      .ve-shell { height: 100vh; display: grid; grid-template-columns: 320px 1fr; gap: 12px; padding: 12px; }
      .ve-panel { background: var(--panel); border: 1px solid var(--border); border-radius: 14px; overflow: hidden; }
      .ve-head { padding: 10px 12px; font-weight: 800; display:flex; align-items:center; justify-content: space-between; gap: 10px; background: rgba(0,0,0,0.22); border-bottom: 1px solid var(--border); }
      .ve-sub { font-size: 12px; color: var(--muted); font-weight: 600; }
      .ve-list { padding: 10px; display:flex; flex-direction: column; gap: 8px; }
      .ve-item { padding: 10px; border-radius: 12px; background: var(--panel2); border: 1px solid rgba(255,255,255,0.10); cursor: grab; user-select: none; }
      .ve-item[data-active="true"] { border-color: rgba(124,58,237,0.65); box-shadow: 0 0 0 3px rgba(124,58,237,0.18); }
      .ve-row { display:flex; align-items:center; justify-content: space-between; gap: 10px; }
      .ve-title { font-weight: 800; outline: none; padding: 4px 6px; border-radius: 8px; }
      .ve-title:focus { background: rgba(255,255,255,0.08); }
      .ve-actions { display:flex; gap: 6px; align-items:center; }
      .ve-btn { border: 1px solid rgba(255,255,255,0.16); background: rgba(0,0,0,0.18); color: var(--text); padding: 6px 8px; border-radius: 10px; font-weight: 800; cursor: pointer; }
      .ve-btn:hover { background: rgba(0,0,0,0.28); }
      .ve-toolbar { display:flex; gap: 6px; flex-wrap: wrap; margin: 10px 0 10px 0; }
      .ve-tool { border: 1px solid rgba(255,255,255,0.16); background: rgba(0,0,0,0.18); color: var(--text); padding: 6px 8px; border-radius: 10px; font-weight: 900; cursor: pointer; font-size: 12px; }
      .ve-tool:hover { background: rgba(0,0,0,0.28); }
      .ve-main { padding: 12px; display:flex; flex-direction: column; gap: 10px; }
      .ve-card { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.10); border-radius: 14px; padding: 12px; }
      .ve-h1 { font-size: 24px; font-weight: 900; margin: 0 0 6px 0; outline:none; padding: 4px 6px; border-radius: 10px; }
      .ve-h1:focus { background: rgba(255,255,255,0.08); }
      .ve-content { line-height: 1.6; color: rgba(255,255,255,0.84); outline:none; min-height: 240px; padding: 8px 10px; border-radius: 12px; }
      .ve-content:focus { background: rgba(255,255,255,0.08); }
      .ve-section-list { display:flex; flex-direction: column; gap: 10px; margin-top: 10px; }
      .ve-section { border-radius: 12px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.04); overflow: hidden; }
      .ve-section-head { display:flex; align-items:center; justify-content: space-between; gap: 10px; padding: 8px 10px; background: rgba(0,0,0,0.18); border-bottom: 1px solid rgba(255,255,255,0.10); }
      .ve-section-title { font-weight: 900; font-size: 12px; color: rgba(255,255,255,0.86); display:flex; align-items:center; gap: 8px; }
      .ve-handle { cursor: grab; user-select: none; opacity: 0.9; }
      .ve-section-actions { display:flex; gap: 8px; }
      .ve-section-body { padding: 10px; }
      .ve-section-body [contenteditable="true"] { outline: none; border-radius: 10px; padding: 8px 10px; min-height: 44px; line-height: 1.6; color: rgba(255,255,255,0.84); }
      .ve-section-body [contenteditable="true"]:focus { background: rgba(255,255,255,0.08); }
      .ve-hint { font-size: 12px; color: var(--muted); }
      .ve-pill { display:inline-flex; align-items:center; gap: 6px; padding: 6px 10px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.06); font-size: 12px; font-weight: 800; color: rgba(255,255,255,0.86); }
      @media (max-width: 900px) { .ve-shell { grid-template-columns: 1fr; } }
    `;

    const html = `
      <div class="ve-shell">
        <div class="ve-panel">
          <div class="ve-head">
            <div>
              <div>Pages</div>
              <div class="ve-sub">Drag to reorder â€¢ Click to edit</div>
            </div>
            <div class="ve-pill">Live editing</div>
          </div>
          <div id="veList" class="ve-list"></div>
        </div>
        <div class="ve-panel">
          <div class="ve-head">
            <div>
              <div>Preview</div>
              <div class="ve-sub">Edits sync back to Zolaid</div>
            </div>
            <div class="ve-hint">Tip: click text to edit</div>
          </div>
          <div class="ve-main">
            <div class="ve-card">
              <h1 id="veTitle" class="ve-h1" contenteditable="true" spellcheck="false"></h1>
              <div class="ve-toolbar" aria-label="Formatting toolbar">
                <button class="ve-tool" type="button" data-cmd="bold"><b>B</b></button>
                <button class="ve-tool" type="button" data-cmd="italic"><i>I</i></button>
                <button class="ve-tool" type="button" data-cmd="formatBlock" data-value="h2">H2</button>
                <button class="ve-tool" type="button" data-cmd="formatBlock" data-value="h3">H3</button>
                <button class="ve-tool" type="button" data-cmd="insertUnorderedList">â€¢ List</button>
                <button class="ve-tool" type="button" data-cmd="insertOrderedList">1. List</button>
                <button class="ve-tool" type="button" data-cmd="justifyLeft">âŸ¸</button>
                <button class="ve-tool" type="button" data-cmd="justifyCenter">â‰¡</button>
                <button class="ve-tool" type="button" data-cmd="justifyRight">âŸ¹</button>
                <button class="ve-tool" type="button" data-action="textColor">Text color</button>
                <button class="ve-tool" type="button" data-action="highlight">Highlight</button>
                <button class="ve-tool" type="button" data-action="link">Link</button>
                <button class="ve-tool" type="button" data-action="image">Image</button>
                <button class="ve-tool" type="button" data-action="video">Video</button>
                <button class="ve-tool" type="button" data-cmd="formatBlock" data-value="blockquote">Quote</button>
                <button class="ve-tool" type="button" data-cmd="removeFormat">Clear</button>
              </div>
              <div style="display:flex; gap:8px; flex-wrap:wrap; margin-top: 6px;">
                <button class="ve-tool" type="button" data-action="add_section" data-kind="paragraph">+ Paragraph</button>
                <button class="ve-tool" type="button" data-action="add_section" data-kind="heading">+ Heading</button>
                <button class="ve-tool" type="button" data-action="add_section" data-kind="image">+ Image</button>
                <button class="ve-tool" type="button" data-action="add_section" data-kind="cta">+ Button / CTA</button>
              </div>
              <div id="veSections" class="ve-section-list"></div>
              <div class="ve-hint" style="margin-top: 10px;">Edit sections directly. Drag sections to reorder.</div>
            </div>
          </div>
        </div>
      </div>
      <script>
        window.__ZOLAID_INITIAL_PAGES__ = ${JSON.stringify(initialPages)};
        window.__ZOLAID_API_URL__ = ${JSON.stringify(process.env.REACT_APP_API_URL || "")};
        window.__ZOLAID_SEGMENT__ = ${JSON.stringify(String(segmentKey || "default"))};
      </script>
    `;

    const js = `
      (function () {
        const send = (payload) => {
          try {
            parent.postMessage({ __zolaidPreview: true, payload }, '*');
          } catch (_) {}
        };

        const segmentKey = (window.__ZOLAID_SEGMENT__ && String(window.__ZOLAID_SEGMENT__)) ? String(window.__ZOLAID_SEGMENT__) : 'default';
        let pages = Array.isArray(window.__ZOLAID_INITIAL_PAGES__) ? window.__ZOLAID_INITIAL_PAGES__.slice() : [];
        pages = pages.filter(p => p && p.id);
        if (pages.length === 0) {
          pages = [{ id: 'page_empty', title: 'Home', content: 'Add pages in Zolaid to edit them here.', contentHtml: '<p>Add pages in Zolaid to edit them here.</p>', sections: [], visible: true }];
        }

        let activeId = (pages.find(p => p.visible) || pages[0]).id;
        let draggingId = null;

        const elList = document.getElementById('veList');
        const elTitle = document.getElementById('veTitle');
        const elToolbar = document.querySelector('.ve-toolbar');
        const elSections = document.getElementById('veSections');

        const getActive = () => pages.find(p => p.id === activeId) || pages[0];

        const renderList = () => {
          elList.innerHTML = '';
          pages.forEach((p) => {
            const item = document.createElement('div');
            item.className = 've-item';
            item.draggable = true;
            item.dataset.pageId = p.id;
            item.dataset.active = (p.id === activeId) ? 'true' : 'false';

            item.addEventListener('dragstart', () => { draggingId = p.id; });
            item.addEventListener('dragend', () => { draggingId = null; });
            item.addEventListener('dragover', (e) => { e.preventDefault(); });
            item.addEventListener('drop', (e) => {
              e.preventDefault();
              if (!draggingId || draggingId === p.id) return;
              const from = pages.findIndex(x => x.id === draggingId);
              const to = pages.findIndex(x => x.id === p.id);
              if (from === -1 || to === -1) return;
              const moved = pages.splice(from, 1)[0];
              pages.splice(to, 0, moved);
              send({ type: 'editor', action: 'reorder_pages', activeId: draggingId, overId: p.id });
              renderList();
            });

            item.addEventListener('click', () => {
              activeId = p.id;
              renderList();
              renderActive();
              send({ type: 'editor', action: 'select_page', pageId: p.id });
            });

            const row = document.createElement('div');
            row.className = 've-row';

            const title = document.createElement('div');
            title.className = 've-title';
            title.contentEditable = 'true';
            title.spellcheck = false;
            title.textContent = p.title || 'Untitled';
            title.addEventListener('click', (e) => e.stopPropagation());
            title.addEventListener('blur', () => {
              const next = (title.textContent || '').trim();
              p.title = next;
              if (p.id === activeId) elTitle.textContent = next || 'Untitled';
              send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { title: next } });
            });

            const actions = document.createElement('div');
            actions.className = 've-actions';

            const toggle = document.createElement('button');
            toggle.className = 've-btn';
            toggle.type = 'button';
            toggle.textContent = p.visible ? 'Hide' : 'Show';
            toggle.addEventListener('click', (e) => {
              e.stopPropagation();
              p.visible = !p.visible;
              toggle.textContent = p.visible ? 'Hide' : 'Show';
              if (!p.visible && p.id === activeId) {
                activeId = (pages.find(x => x.visible) || pages[0]).id;
              }
              renderList();
              renderActive();
              send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { visible: p.visible } });
            });

            actions.appendChild(toggle);
            row.appendChild(title);
            row.appendChild(actions);
            item.appendChild(row);
            elList.appendChild(item);
          });
        };

        const makeSectionId = () => 'sec_' + Date.now() + '_' + Math.random().toString(16).slice(2);

        const normalizeSections = (p) => {
          const existing = Array.isArray(p.sections) ? p.sections : [];
          if (existing.length > 0) {
            return existing.map((s) => ({
              id: String(s?.id || makeSectionId()),
              type: String(s?.type || 'paragraph'),
              contentHtml: sanitizeHtml(String(s?.contentHtml || s?.content || '')),
              variants: (s && typeof s.variants === 'object' && s.variants) ? s.variants : {},
            }));
          }
          // Fallback: derive from contentHtml/content
          const fallbackHtml = (p.contentHtml && String(p.contentHtml)) ? String(p.contentHtml) : '';
          const html = fallbackHtml
            ? fallbackHtml
            : ('<p>' + String(p.content || '').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</p>');
          return [{ id: makeSectionId(), type: 'paragraph', contentHtml: sanitizeHtml(html), variants: {} }];
        };

        const materializeFromSections = (sections, forSegment = 'default') => {
          const html = (sections || []).map(s => {
            const v = s && s.variants && typeof s.variants === 'object' ? s.variants : null;
            const segHtml = (forSegment !== 'default' && v && typeof v[forSegment] === 'string') ? v[forSegment] : null;
            return String((segHtml != null ? segHtml : (s.contentHtml || '')) || '');
          }).join('\\n');
          const tpl = document.createElement('template');
          tpl.innerHTML = html;
          const text = (tpl.content.textContent || '').trim();
          return { html, text };
        };

        const renderSections = () => {
          const p = getActive();
          p.sections = normalizeSections(p);
          if (!elSections) return;
          elSections.innerHTML = '';

          let draggingSectionId = null;

          p.sections.forEach((s) => {
            const wrap = document.createElement('div');
            wrap.className = 've-section';
            wrap.draggable = true;
            wrap.dataset.sectionId = s.id;

            wrap.addEventListener('dragstart', () => { draggingSectionId = s.id; });
            wrap.addEventListener('dragend', () => { draggingSectionId = null; });
            wrap.addEventListener('dragover', (e) => { e.preventDefault(); });
            wrap.addEventListener('drop', (e) => {
              e.preventDefault();
              if (!draggingSectionId || draggingSectionId === s.id) return;
              const secs = p.sections;
              const from = secs.findIndex(x => String(x.id) === String(draggingSectionId));
              const to = secs.findIndex(x => String(x.id) === String(s.id));
              if (from === -1 || to === -1) return;
              const moved = secs.splice(from, 1)[0];
              secs.splice(to, 0, moved);
              const matDefault = materializeFromSections(secs, 'default');
              p.contentHtml = matDefault.html;
              p.content = matDefault.text;
              send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { sections: secs, contentHtml: matDefault.html, content: matDefault.text } });
              renderSections();
            });

            const head = document.createElement('div');
            head.className = 've-section-head';

            const title = document.createElement('div');
            title.className = 've-section-title';
            title.innerHTML = '<span class="ve-handle">â†•</span><span>' + (s.type || 'section') + '</span>';

            const actions = document.createElement('div');
            actions.className = 've-section-actions';

            const del = document.createElement('button');
            del.className = 've-btn danger';
            del.type = 'button';
            del.textContent = 'Remove';
            del.addEventListener('click', (e) => {
              e.stopPropagation();
              p.sections = p.sections.filter(x => String(x.id) !== String(s.id));
              const matDefault = materializeFromSections(p.sections, 'default');
              p.contentHtml = matDefault.html;
              p.content = matDefault.text;
              send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { sections: p.sections, contentHtml: matDefault.html, content: matDefault.text } });
              renderSections();
            });

            actions.appendChild(del);
            head.appendChild(title);
            head.appendChild(actions);

            const body = document.createElement('div');
            body.className = 've-section-body';

            const ed = document.createElement('div');
            ed.contentEditable = 'true';
            ed.spellcheck = true;
            {
              const v = s && s.variants && typeof s.variants === 'object' ? s.variants : null;
              const segHtml = (segmentKey !== 'default' && v && typeof v[segmentKey] === 'string') ? v[segmentKey] : null;
              ed.innerHTML = String((segHtml != null ? segHtml : (s.contentHtml || '')) || '');
            }
            ed.addEventListener('focus', () => { window.__ZOLAID_ACTIVE_EDITOR__ = ed; });
            ed.addEventListener('blur', () => {
              const nextHtml = sanitizeHtml(ed.innerHTML || '');
              if (segmentKey !== 'default') {
                s.variants = (s.variants && typeof s.variants === 'object') ? s.variants : {};
                s.variants[segmentKey] = nextHtml;
              } else {
                s.contentHtml = nextHtml;
              }
              const matDefault = materializeFromSections(p.sections, 'default');
              p.contentHtml = matDefault.html;
              p.content = matDefault.text;
              send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { sections: p.sections, contentHtml: matDefault.html, content: matDefault.text } });
            });

            // Drag/drop local image into section
            ed.addEventListener('dragover', (e) => { e.preventDefault(); });
            ed.addEventListener('drop', (e) => {
              try {
                e.preventDefault();
                const dt = e.dataTransfer;
                const files = dt && dt.files ? Array.from(dt.files) : [];
                const img = files.find(f => /^image\\//i.test(f.type || ''));
                if (!img) return;
                const apiBase = (window.__ZOLAID_API_URL__ || '').trim();
                const endpoint = apiBase
                  ? (apiBase.replace(/\\/+$/, '') + '/website/upload-asset')
                  : '/website/upload-asset';
                const form = new FormData();
                form.append('file', img, img.name || 'image.png');
                fetch(endpoint, { method: 'POST', body: form })
                  .then(r => r.json())
                  .then(data => {
                    const url = data && data.url ? String(data.url) : '';
                    if (!url) return;
                    const finalUrl = apiBase && url.startsWith('/') ? (apiBase.replace(/\\/+$/, '') + url) : url;
                    ed.focus();
                    try { document.execCommand('insertImage', false, finalUrl); } catch (_) {}
                    try { ed.dispatchEvent(new Event('blur')); } catch (_) {}
                  })
                  .catch((_e) => {});
              } catch (_) {}
            });

            body.appendChild(ed);
            wrap.appendChild(head);
            wrap.appendChild(body);
            elSections.appendChild(wrap);
          });
        };

        const renderActive = () => {
          const p = getActive();
          elTitle.textContent = (p.title || 'Untitled');
          renderSections();
        };

        const sanitizeHtml = (html) => {
          try {
            const tpl = document.createElement('template');
            tpl.innerHTML = String(html || '');
            // Remove scripts
            tpl.content.querySelectorAll('script').forEach(n => n.remove());
            // Drop event handler attributes (on*)
            tpl.content.querySelectorAll('*').forEach((el) => {
              [...el.attributes].forEach((attr) => {
                if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
              });
            });
            return tpl.innerHTML;
          } catch (_) {
            return '';
          }
        };

        const exec = (cmd, value) => {
          try { document.execCommand(cmd, false, value); } catch (_) {}
        };

        if (elToolbar) {
          elToolbar.addEventListener('click', (e) => {
            const btn = e.target && e.target.closest ? e.target.closest('button') : null;
            if (!btn) return;
            const action = btn.getAttribute('data-action');
            if (action === 'link') {
              const url = prompt('Enter URL (https://...)');
              if (!url) return;
              const ed = window.__ZOLAID_ACTIVE_EDITOR__;
              if (ed) ed.focus();
              exec('createLink', url);
              return;
            }
            if (action === 'image') {
              const url = prompt('Enter image URL (https://...)');
              if (!url) return;
              const ed = window.__ZOLAID_ACTIVE_EDITOR__;
              if (ed) ed.focus();
              exec('insertImage', url);
              // sync happens on blur; also trigger a lightweight sync now
              try { if (ed) ed.dispatchEvent(new Event('blur')); } catch (_) {}
              return;
            }
            if (action === 'video') {
              const url = prompt('Enter video URL (https://...)');
              if (!url) return;
              const ed = window.__ZOLAID_ACTIVE_EDITOR__;
              if (ed) ed.focus();
              const safeUrl = String(url);
              const html = '<p><video controls src="' + safeUrl.replace(/"/g, '&quot;') + '" style="max-width:100%;border-radius:12px;"></video></p>';
              exec('insertHTML', html);
              try { if (ed) ed.dispatchEvent(new Event('blur')); } catch (_) {}
              return;
            }
            if (action === 'textColor') {
              const color = prompt('Text color (hex like #111827)');
              if (!color) return;
              const ed = window.__ZOLAID_ACTIVE_EDITOR__;
              if (ed) ed.focus();
              exec('styleWithCSS', true);
              exec('foreColor', String(color));
              try { if (ed) ed.dispatchEvent(new Event('blur')); } catch (_) {}
              return;
            }
            if (action === 'highlight') {
              const color = prompt('Highlight color (hex like #fde68a)');
              if (!color) return;
              const ed = window.__ZOLAID_ACTIVE_EDITOR__;
              if (ed) ed.focus();
              exec('styleWithCSS', true);
              // Some browsers prefer hiliteColor, others backColor
              exec('hiliteColor', String(color));
              exec('backColor', String(color));
              try { if (ed) ed.dispatchEvent(new Event('blur')); } catch (_) {}
              return;
            }
            if (action === 'add_section') {
              const kind = btn.getAttribute('data-kind') || 'paragraph';
              const p = getActive();
              p.sections = normalizeSections(p);
              if (kind === 'heading') {
                p.sections.push({ id: makeSectionId(), type: 'heading', contentHtml: '<h2>New section</h2>' });
              } else if (kind === 'image') {
                p.sections.push({ id: makeSectionId(), type: 'image', contentHtml: '<p><img alt="Image" src="https://placehold.co/1200x600" style="max-width:100%;height:auto;border-radius:12px;" /></p>' });
              } else if (kind === 'cta') {
                p.sections.push({
                  id: makeSectionId(),
                  type: 'cta',
                  contentHtml:
                    '<p style="margin:0.25rem 0;"><a href="#contact" style="display:inline-block;padding:12px 16px;border-radius:12px;background:#7c3aed;color:#ffffff;text-decoration:none;font-weight:900;">Get Started</a></p><p style="opacity:0.8;margin:0.5rem 0 0 0;">Tip: edit the button text/link, then drag this section anywhere.</p>',
                });
              } else {
                p.sections.push({ id: makeSectionId(), type: 'paragraph', contentHtml: '<p>New paragraph...</p>' });
              }
              const matDefault = materializeFromSections(p.sections, 'default');
              p.contentHtml = matDefault.html;
              p.content = matDefault.text;
              send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { sections: p.sections, contentHtml: matDefault.html, content: matDefault.text } });
              renderSections();
              return;
            }
            const cmd = btn.getAttribute('data-cmd');
            if (!cmd) return;
            const value = btn.getAttribute('data-value');
            if (cmd === 'formatBlock' && value) {
              const ed = window.__ZOLAID_ACTIVE_EDITOR__;
              if (ed) ed.focus();
              exec('formatBlock', '<' + value + '>');
              return;
            }
            const ed = window.__ZOLAID_ACTIVE_EDITOR__;
            if (ed) ed.focus();
            exec(cmd);
          });
        }

        const syncActiveEdits = () => {
          const p = getActive();
          if (!p) return;
          const nextTitle = (elTitle.textContent || '').trim();
          if (nextTitle !== (p.title || '')) {
            p.title = nextTitle;
            send({ type: 'editor', action: 'update_page', pageId: p.id, patch: { title: nextTitle } });
            renderList();
          }
        };

        elTitle.addEventListener('blur', syncActiveEdits);

        renderList();
        renderActive();
        send({ type: 'editor', action: 'ready' });
      })();
    `;

    return { html, css, js };
  };

  const addStandardPagesToWebsiteData = () => {
    setWebsiteData((prev) => {
      const selected = prev.standardPages || {};
      const existingPages = getOrderedPages(prev);
      const existingTitles = new Set(existingPages.map((p) => String(p?.title || "").trim().toLowerCase()).filter(Boolean));

      const standardPages = buildStandardBusinessPages({
        standardPages: prev.standardPages,
        paymentsEnabled: Boolean(prev.payments?.enabled),
      });

      // Remove previously auto-generated standard pages that are no longer selected.
      // We only remove pages with an explicit standardKey marker to avoid deleting user-created pages.
      const keepById = { ...(prev.pagesById || {}) };
      const keepOrder = [...(prev.pageOrder || [])];
      const shouldKeepStandard = (key) => {
        if (key === "refund") return Boolean(prev.payments?.enabled) && Boolean(selected.refund);
        return Boolean(selected[key]);
      };
      for (const id of Object.keys(keepById)) {
        const pg = keepById[id];
        const key = String(pg?.standardKey || "");
        if (!key) continue;
        if (!shouldKeepStandard(key)) {
          delete keepById[id];
        }
      }
      const nextOrderFiltered = keepOrder.filter((id) => Boolean(keepById[id]));

      const toAdd = standardPages.filter((p) => !existingTitles.has(String(p.title).trim().toLowerCase()));
      if (toAdd.length === 0) {
        // Still return the filtered removal if any happened.
        return { ...prev, pagesById: keepById, pageOrder: nextOrderFiltered };
      }

      const nextById = { ...keepById };
      const nextOrder = [...nextOrderFiltered];
      toAdd.forEach((p) => {
        const id = makePageId();
        nextById[id] = { ...p, id };
        nextOrder.push(id);
      });

      return {
        ...prev,
        pagesById: nextById,
        pageOrder: nextOrder,
      };
    });
  };

  const savePage = () => {
    if (!currentPage.title.trim()) {
      setError("Please enter a page title.");
      return;
    }
    addPage({ ...currentPage });
    setCurrentPage({ title: "", content: "", sections: [] });
    setStep(4);
  };

  const generateWebsite = async () => {
    try {
      if (validation.errors.length > 0) return;
      setLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const endpoint = apiUrl ? `${apiUrl}/website/generate` : '/website/generate';
      const websiteDataForGeneration = {
        ...websiteData,
        pages: getOrderedPages(websiteData),
      };
      const response = await axios.post(endpoint, {
        websiteData: websiteDataForGeneration,
        token,
      });

      // Prefer structured artifacts if available; fall back to code/html
      const artifacts = response.data?.artifacts && typeof response.data.artifacts === 'object'
        ? response.data.artifacts
        : null;
      if (artifacts && (artifacts.html || artifacts.css || artifacts.js)) {
        setGeneratedArtifacts({
          html: artifacts.html || "",
          css: artifacts.css || "",
          js: artifacts.js || "",
        });
      }
      if (typeof response.data?.code === 'string') {
        setGeneratedHtmlDoc(response.data.code);
      }
      if (response.data?.files && typeof response.data.files === "object") {
        setGeneratedFiles(response.data.files);
      } else {
        setGeneratedFiles(null);
      }
      if (typeof response.data?.instructions === "string") {
        setGeneratedInstructions(response.data.instructions);
      } else {
        setGeneratedInstructions("");
      }

      // Save version snapshot
      setVersions((prev) => [
        {
          id: Date.now(),
          ts: new Date().toISOString(),
          websiteData: websiteDataForGeneration,
          artifacts: artifacts ? { html: artifacts.html || "", css: artifacts.css || "", js: artifacts.js || "" } : null,
          htmlDoc: typeof response.data?.code === "string" ? response.data.code : "",
          files: response.data?.files && typeof response.data.files === "object" ? response.data.files : null,
          instructions: typeof response.data?.instructions === "string" ? response.data.instructions : "",
        },
        ...prev,
      ].slice(0, 25));

      // Always push generated output into ZolaidComputer preview (if embedded).
      try {
        if (typeof onSendToComputerPreview === "function") {
          const htmlDoc = typeof response.data?.code === "string" ? response.data.code : "";
          const files = response.data?.files && typeof response.data.files === "object" ? response.data.files : null;
          const merged = {
            ...(files || {}),
            ...(htmlDoc ? { "index.html": htmlDoc } : {}),
            ...(artifacts && typeof artifacts === "object"
              ? {
                  "styles.css": String(artifacts.css || ""),
                  "app.js": String(artifacts.js || ""),
                }
              : {}),
          };
          onSendToComputerPreview({
            html: htmlDoc,
            css: artifacts?.css || "",
            js: artifacts?.js || "",
            files: merged,
            source: "builder_generate",
          });
        }
      } catch (_) {}

      if (response.data.websiteUrl) {
        window.open(response.data.websiteUrl, "_blank");
      } else {
        setError("Failed to generate website. Please try again.");
      }
    } catch (err) {
      console.error("Website generation error:", err);
      setError(err.response?.data?.error || "Failed to generate website.");
    } finally {
      setLoading(false);
    }
  };

  

  

  const handlePreviewRuntimeEvent = (payload) => {
    if (!payload || typeof payload !== "object") return;
    if (payload.type !== "editor") return;
    const action = payload.action;
    if (action === "update_page") {
      const pageId = payload.pageId;
      const patch = payload.patch;
      updatePage(pageId, patch);
      return;
    }
    if (action === "reorder_pages") {
      reorderPages(payload.activeId, payload.overId);
      return;
    }
    if (action === "select_page") {
      if (payload.pageId) setActiveVisualPageId(String(payload.pageId));
      return;
    }
    if (action === "ready") {
      // pick a default active page
      const first = getOrderedPages(websiteData).find((p) => p?.visible !== false) || getOrderedPages(websiteData)[0];
      if (first?.id) setActiveVisualPageId(String(first.id));
    }
  };

  const handlePreviewRuntimeEventWithAnalytics = (payload) => {
    if (!payload || typeof payload !== "object") return;
    if (payload.type === "analytics_session" && payload.sessionId) {
      setPreviewSessionId(String(payload.sessionId));
      return;
    }
    if (payload.type === "analytics" && payload.event) {
      const ev = payload.event;
      setKpis((prev) => {
        const next = { ...prev };
        if (String(ev.type) === "click") next.clicks = (next.clicks || 0) + 1;
        if (String(ev.type) === "scroll") {
          const pct = Number(ev.scrollPct);
          if (Number.isFinite(pct)) next.maxScrollPct = Math.max(next.maxScrollPct || 0, pct);
        }
        return next;
      });

      // Best-effort persist to backend
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const endpoint = apiUrl ? `${apiUrl}/analytics/event` : "/analytics/event";
        const sid = previewSessionId || String(payload.sessionId || "");
        if (sid) {
          axios.post(endpoint, { sessionId: sid, projectId: currentProjectId || undefined, event: ev }).catch(() => {});
        }
      } catch (_) {}
      return;
    }
    if (payload.type === "lead" && payload.lead) {
      // Best-effort persist to backend + update local list
      const lead = payload.lead;
      setLeads((prev) => [{ ...lead, receivedAt: Date.now() }, ...prev].slice(0, 50));
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const endpoint = apiUrl ? `${apiUrl}/automation/lead` : "/automation/lead";
        const sid = previewSessionId || String(payload.sessionId || "");
        if (sid) {
          axios.post(endpoint, { sessionId: sid, lead }).catch(() => {});
        }
      } catch (_) {}
      return;
    }
    // fall back to editor events
    handlePreviewRuntimeEvent(payload);
  };

  const refreshLeads = async () => {
    if (!previewSessionId) return;
    try {
      setLeadsError("");
      setLeadsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/automation/leads` : "/automation/leads";
      const resp = await axios.get(endpoint, { params: { sessionId: previewSessionId } });
      setLeads(Array.isArray(resp.data?.leads) ? resp.data.leads : []);
    } catch (e) {
      console.error("Fetch leads failed:", e);
      setLeadsError(e?.response?.data?.error || "Failed to load leads.");
    } finally {
      setLeadsLoading(false);
    }
  };

  const autoOptimizeFromKpis = () => {
    const page = getActiveVisualPage();
    if (!page) return;
    const goal = [
      "Optimize this page based on observed engagement KPIs.",
      `KPIs: clicks=${Number(kpis.clicks || 0)}, maxScrollPct=${Number(kpis.maxScrollPct || 0)}%.`,
      "If scroll is low, shorten and move key CTA earlier. If clicks are low, improve headline + CTA clarity.",
      "Keep changes safe and reversible; prefer section edits.",
    ].join(" ");
    setAgentGoal(goal);
    // immediately propose A/B based on KPIs
    proposeAgentPlanVariants();
  };

  const suggestGoalFromKpis = async () => {
    try {
      if (!previewSessionId) return;
      setKpiGoalError("");
      setKpiGoalLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/analytics/suggest-goal` : "/analytics/suggest-goal";
      const page = getActiveVisualPage();
      const resp = await axios.post(endpoint, {
        sessionId: previewSessionId,
        websiteName: websiteData?.name || "",
        pageTitle: page?.title || "",
        context: {
          brand: websiteData?.brand || {},
          paymentsEnabled: Boolean(websiteData?.payments?.enabled),
        },
      });
      const goal = String(resp.data?.goal || "").trim();
      if (goal) {
        setAgentGoal(goal);
        proposeAgentPlanVariants();
      }
    } catch (e) {
      console.error("Suggest goal from KPIs failed:", e);
      setKpiGoalError(e?.response?.data?.error || "Failed to suggest goal.");
    } finally {
      setKpiGoalLoading(false);
    }
  };

  const suggestGoalFromProjectKpis = async () => {
    try {
      if (!currentProjectId) return;
      setKpiGoalError("");
      setKpiGoalLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/analytics/project-suggest-goal` : "/analytics/project-suggest-goal";
      const page = getActiveVisualPage();
      const resp = await axios.post(endpoint, {
        projectId: currentProjectId,
        websiteName: websiteData?.name || "",
        pageTitle: page?.title || "",
        context: {
          brand: websiteData?.brand || {},
          paymentsEnabled: Boolean(websiteData?.payments?.enabled),
        },
      });
      const goal = String(resp.data?.goal || "").trim();
      if (goal) {
        setAgentGoal(goal);
        proposeAgentPlanVariants();
      }
    } catch (e) {
      console.error("Suggest goal from Project KPIs failed:", e);
      setKpiGoalError(e?.response?.data?.error || "Failed to suggest project goal.");
    } finally {
      setKpiGoalLoading(false);
    }
  };

  const refreshProjectKpis = async () => {
    try {
      if (!currentProjectId) return;
      setProjectKpisError("");
      setProjectKpisLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/analytics/project-summary` : "/analytics/project-summary";
      const resp = await axios.get(endpoint, { params: { projectId: currentProjectId } });
      setProjectKpis(resp.data || null);
    } catch (e) {
      console.error("project kpis failed:", e);
      setProjectKpisError(e?.response?.data?.error || "Failed to load project KPIs.");
      setProjectKpis(null);
    } finally {
      setProjectKpisLoading(false);
    }
  };

  const resetProjectKpis = async () => {
    try {
      if (!currentProjectId) return;
      const ok = window.confirm("Reset project KPIs? (prototype)");
      if (!ok) return;
      setProjectKpisError("");
      setProjectKpisLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/analytics/project-reset` : "/analytics/project-reset";
      await axios.post(endpoint, { projectId: currentProjectId });
      await refreshProjectKpis();
    } catch (e) {
      console.error("reset project kpis failed:", e);
      setProjectKpisError(e?.response?.data?.error || "Failed to reset project KPIs.");
    } finally {
      setProjectKpisLoading(false);
    }
  };

  const refreshAutoOptimizeSchedule = async () => {
    try {
      if (!currentProjectId) return;
      setAutoOptError("");
      setAutoOptLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/optimizer/schedules` : "/optimizer/schedules";
      const resp = await axios.get(endpoint, { params: { projectId: currentProjectId } });
      const s = Array.isArray(resp.data?.schedules) ? resp.data.schedules[0] : null;
      if (s) {
        setAutoOptEnabled(Boolean(s.enabled));
        setAutoOptIntervalMin(Number(s.intervalMinutes || 60));
        setAutoOptHistory(Array.isArray(s.history) ? s.history : []);
      } else {
        setAutoOptHistory([]);
      }
    } catch (e) {
      console.error("refresh auto-opt schedule failed:", e);
      setAutoOptError(e?.response?.data?.error || "Failed to load schedule.");
    } finally {
      setAutoOptLoading(false);
    }
  };

  const setAutoOptimizeSchedule = async (enabled, intervalMinutes) => {
    try {
      if (!currentProjectId) return;
      setAutoOptError("");
      setAutoOptLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/optimizer/schedule` : "/optimizer/schedule";
      const resp = await axios.post(endpoint, {
        projectId: currentProjectId,
        enabled: Boolean(enabled),
        intervalMinutes: Number(intervalMinutes || 60),
      });
      const s = resp.data?.schedule;
      if (s) {
        setAutoOptEnabled(Boolean(s.enabled));
        setAutoOptIntervalMin(Number(s.intervalMinutes || 60));
        setAutoOptHistory(Array.isArray(s.history) ? s.history : []);
      }
    } catch (e) {
      console.error("set auto-opt schedule failed:", e);
      setAutoOptError(e?.response?.data?.error || "Failed to save schedule.");
    } finally {
      setAutoOptLoading(false);
    }
  };

  const runAutoOptimizeNow = async () => {
    try {
      if (!currentProjectId) return;
      setAutoOptError("");
      setAutoOptLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/optimizer/run` : "/optimizer/run";
      const page = getActiveVisualPage();
      const resp = await axios.post(endpoint, {
        projectId: currentProjectId,
        websiteName: websiteData?.name || "",
        pageTitle: page?.title || "",
        context: { brand: websiteData?.brand || {} },
      });
      const goal = String(resp.data?.recommendation?.goal || "").trim();
      const hist = resp.data?.schedule?.history;
      if (Array.isArray(hist)) setAutoOptHistory(hist);
      if (goal) {
        setAgentGoal(goal);
        proposeAgentPlanVariants();
      }
    } catch (e) {
      console.error("auto-opt run failed:", e);
      setAutoOptError(e?.response?.data?.error || "Failed to run auto-optimize.");
    } finally {
      setAutoOptLoading(false);
    }
  };

  const refreshAssetLibrary = async () => {
    try {
      setAssetLibraryError("");
      setAssetLibraryLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/assets` : "/website/assets";
      const resp = await axios.get(endpoint);
      setAssetLibrary(Array.isArray(resp.data?.assets) ? resp.data.assets : []);
    } catch (e) {
      console.error("asset library failed:", e);
      setAssetLibraryError(e?.response?.data?.error || "Failed to load assets.");
      setAssetLibrary([]);
    } finally {
      setAssetLibraryLoading(false);
    }
  };

  const insertAssetAsImageSection = (assetUrl) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const url = String(assetUrl || "");
      const finalUrl = apiUrl && url.startsWith("/") ? `${apiUrl.replace(/\/+$/, "")}${url}` : url;
      const page = getActiveVisualPage();
      if (!page) return;
      const sections = Array.isArray(page.sections) ? [...page.sections] : [];
      const sec = {
        id: `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        type: "image",
        contentHtml: `<p><img src="${finalUrl}" alt="Asset" style="max-width:100%;height:auto;border-radius:12px;" /></p>`,
        variants: {},
      };
      sections.unshift(sec);
      const mat = materializeFromSections(sections);
      updatePage(page.id, { sections, contentHtml: mat.contentHtml, content: mat.content });
      setTimeout(() => setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), audienceSegment)), 0);
    } catch (_) {}
  };

  const runComplianceCheck = async () => {
    try {
      setComplianceError("");
      setComplianceLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/compliance` : "/website/compliance";
      const payload = {
        ...websiteData,
        pages: getOrderedPages(websiteData),
      };
      const resp = await axios.post(endpoint, { websiteData: payload });
      setComplianceResult({
        errors: Array.isArray(resp.data?.errors) ? resp.data.errors : [],
        warnings: Array.isArray(resp.data?.warnings) ? resp.data.warnings : [],
      });
    } catch (e) {
      console.error("Compliance check failed:", e);
      setComplianceError(e?.response?.data?.error || "Compliance check failed.");
      setComplianceResult(null);
    } finally {
      setComplianceLoading(false);
    }
  };

  const runEmotionScore = async () => {
    const page = getActiveVisualPage();
    if (!page) return;
    try {
      setEmotionError("");
      setEmotionLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/emotion-score` : "/website/emotion-score";
      const resp = await axios.post(endpoint, {
        websiteName: websiteData?.name || "",
        websiteDescription: websiteData?.description || "",
        goal: agentGoal || "",
        page: {
          id: page.id,
          title: page.title,
          sections: Array.isArray(page.sections) ? page.sections : [],
        },
      });
      setEmotionResult(resp.data || null);
    } catch (e) {
      console.error("Emotion score failed:", e);
      setEmotionError(e?.response?.data?.error || "Emotion score failed.");
      setEmotionResult(null);
    } finally {
      setEmotionLoading(false);
    }
  };

  const runImpactSimulation = async () => {
    try {
      const goal = String(impactGoal || "").trim();
      if (!goal) return;
      setImpactError("");
      setImpactLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/impact/simulate` : "/impact/simulate";
      const resp = await axios.post(endpoint, {
        goal,
        context: [
          {
            websiteName: websiteData?.name || "",
            websiteDescription: websiteData?.description || "",
            brand: websiteData?.brand || {},
          },
        ],
      });
      setImpactResult(resp.data || null);
    } catch (e) {
      console.error("Impact simulation failed:", e);
      setImpactError(e?.response?.data?.error || "Impact simulation failed.");
      setImpactResult(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const suggestHeroImage = async () => {
    try {
      setHeroImageError("");
      setHeroImageLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/hero-image` : "/website/hero-image";
      const resp = await axios.post(endpoint, {
        websiteName: websiteData?.name || "",
        websiteDescription: websiteData?.description || "",
        brand: websiteData?.brand || {},
      });
      const url = String(resp.data?.url || "");
      if (!url) throw new Error("No URL returned");
      const finalUrl = apiUrl && url.startsWith("/") ? `${apiUrl.replace(/\/+$/, "")}${url}` : url;

      const page = getActiveVisualPage();
      if (!page) return;
      const sections = Array.isArray(page.sections) ? [...page.sections] : [];
      const sec = {
        id: `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        type: "image",
        contentHtml: `<p><img src="${finalUrl}" alt="Hero image for ${String(websiteData?.name || "website").replace(/"/g, "&quot;")}" style="max-width:100%;height:auto;border-radius:12px;" /></p>`,
        variants: {},
      };
      sections.unshift(sec);
      // Update page with new section; keep default materialization
      const mat = materializeFromSections(sections);
      updatePage(page.id, { sections, contentHtml: mat.contentHtml, content: mat.content });
      setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), audienceSegment));
    } catch (e) {
      console.error("Suggest hero image failed:", e);
      setHeroImageError(e?.response?.data?.error || e?.message || "Failed to generate hero image.");
    } finally {
      setHeroImageLoading(false);
    }
  };

  const getActiveVisualPage = () => {
    const pages = getOrderedPages(websiteData);
    if (activeVisualPageId) {
      const match = pages.find((p) => String(p?.id) === String(activeVisualPageId));
      if (match) return match;
    }
    return pages.find((p) => p?.visible !== false) || pages[0] || null;
  };

  const sanitizeHtmlLight = (html) => {
    try {
      const tpl = document.createElement("template");
      tpl.innerHTML = String(html || "");
      tpl.content.querySelectorAll("script").forEach((n) => n.remove());
      tpl.content.querySelectorAll("*").forEach((el) => {
        [...el.attributes].forEach((attr) => {
          if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
        });
      });
      return tpl.innerHTML;
    } catch (_) {
      return String(html || "");
    }
  };

  const materializeFromSections = (sections) => {
    const secs = Array.isArray(sections) ? sections : [];
    const html = secs.map((s) => String(s?.contentHtml || "")).join("\n");
    try {
      const tpl = document.createElement("template");
      tpl.innerHTML = html;
      const text = String(tpl.content.textContent || "").trim();
      return { contentHtml: sanitizeHtmlLight(html), content: text };
    } catch (_) {
      return { contentHtml: sanitizeHtmlLight(html), content: "" };
    }
  };

  const ensureSectionsForPage = (page) => {
    if (!page) return null;
    const secs = Array.isArray(page.sections) ? page.sections : [];
    if (secs.length > 0) return page;
    const baseHtml = (page.contentHtml && String(page.contentHtml).trim())
      ? String(page.contentHtml)
      : `<p>${String(page.content || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
    const derived = [{ id: `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`, type: "paragraph", contentHtml: sanitizeHtmlLight(baseHtml) }];
    const mat = materializeFromSections(derived);
    updatePage(page.id, { sections: derived, contentHtml: mat.contentHtml, content: mat.content });
    return { ...page, sections: derived, contentHtml: mat.contentHtml, content: mat.content };
  };

  const applyPlanToPage = (pageId, plan) => {
    const id = String(pageId || "");
    if (!id || !plan || !Array.isArray(plan.operations)) return;
    setWebsiteData((prev) => {
      const page = prev.pagesById?.[id];
      if (!page) return prev;
      const ops = plan.operations;
      let sections = Array.isArray(page.sections) ? page.sections.map((s) => ({ ...s })) : [];
      // ensure sections exist
      if (sections.length === 0) {
        const baseHtml = (page.contentHtml && String(page.contentHtml).trim())
          ? String(page.contentHtml)
          : `<p>${String(page.content || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
        sections = [{ id: `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`, type: "paragraph", contentHtml: sanitizeHtmlLight(baseHtml) }];
      }

      const byId = new Map(sections.map((s) => [String(s.id), s]));

      for (const op of ops) {
        const t = String(op?.type || "");
        if (t === "add_section") {
          const sec = op?.section || {};
          const newId = String(sec.id || `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`);
          const newSection = {
            id: newId,
            type: String(sec.type || "paragraph"),
            contentHtml: sanitizeHtmlLight(String(sec.contentHtml || "<p></p>")),
          };
          const afterId = op.afterSectionId ? String(op.afterSectionId) : "";
          if (afterId && byId.has(afterId)) {
            const idx = sections.findIndex((s) => String(s.id) === afterId);
            sections.splice(idx + 1, 0, newSection);
          } else {
            sections.push(newSection);
          }
          byId.set(newId, newSection);
        }
        if (t === "update_section") {
          const sid = String(op.sectionId || "");
          const target = byId.get(sid);
          if (!target) continue;
          const patch = op.patch || {};
          if (patch.type) target.type = String(patch.type);
          if (patch.contentHtml != null) target.contentHtml = sanitizeHtmlLight(String(patch.contentHtml));
        }
        if (t === "delete_section") {
          const sid = String(op.sectionId || "");
          sections = sections.filter((s) => String(s.id) !== sid);
          byId.delete(sid);
        }
        if (t === "reorder_sections") {
          const order = Array.isArray(op.sectionOrder) ? op.sectionOrder.map(String) : [];
          if (order.length > 0) {
            const next = [];
            const seen = new Set();
            for (const sid of order) {
              const s = byId.get(sid);
              if (s) {
                next.push(s);
                seen.add(sid);
              }
            }
            for (const s of sections) {
              const sid = String(s.id);
              if (!seen.has(sid)) next.push(s);
            }
            sections = next;
          }
        }
      }

      const mat = materializeFromSections(sections);
      return {
        ...prev,
        pagesById: {
          ...(prev.pagesById || {}),
          [id]: {
            ...page,
            sections,
            contentHtml: mat.contentHtml,
            content: mat.content,
          },
        },
      };
    });
  };

  const simulatePlanForPage = (page, plan) => {
    if (!page || !plan || !Array.isArray(plan.operations)) {
      return { sections: Array.isArray(page?.sections) ? page.sections : [], contentHtml: page?.contentHtml || "", content: page?.content || "" };
    }
    const ops = plan.operations;
    let sections = Array.isArray(page.sections) ? page.sections.map((s) => ({ ...s })) : [];
    if (sections.length === 0) {
      const baseHtml = (page.contentHtml && String(page.contentHtml).trim())
        ? String(page.contentHtml)
        : `<p>${String(page.content || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
      sections = [{ id: `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`, type: "paragraph", contentHtml: sanitizeHtmlLight(baseHtml) }];
    }

    const byId = new Map(sections.map((s) => [String(s.id), s]));
    for (const op of ops) {
      const t = String(op?.type || "");
      if (t === "add_section") {
        const sec = op?.section || {};
        const newId = String(sec.id || `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`);
        const newSection = {
          id: newId,
          type: String(sec.type || "paragraph"),
          contentHtml: sanitizeHtmlLight(String(sec.contentHtml || "<p></p>")),
        };
        const afterId = op.afterSectionId ? String(op.afterSectionId) : "";
        if (afterId && byId.has(afterId)) {
          const idx = sections.findIndex((s) => String(s.id) === afterId);
          sections.splice(idx + 1, 0, newSection);
        } else {
          sections.push(newSection);
        }
        byId.set(newId, newSection);
      }
      if (t === "update_section") {
        const sid = String(op.sectionId || "");
        const target = byId.get(sid);
        if (!target) continue;
        const patch = op.patch || {};
        if (patch.type) target.type = String(patch.type);
        if (patch.contentHtml != null) target.contentHtml = sanitizeHtmlLight(String(patch.contentHtml));
      }
      if (t === "delete_section") {
        const sid = String(op.sectionId || "");
        sections = sections.filter((s) => String(s.id) !== sid);
        byId.delete(sid);
      }
      if (t === "reorder_sections") {
        const order = Array.isArray(op.sectionOrder) ? op.sectionOrder.map(String) : [];
        if (order.length > 0) {
          const next = [];
          const seen = new Set();
          for (const sid of order) {
            const s = byId.get(sid);
            if (s) {
              next.push(s);
              seen.add(sid);
            }
          }
          for (const s of sections) {
            const sid = String(s.id);
            if (!seen.has(sid)) next.push(s);
          }
          sections = next;
        }
      }
    }
    const mat = materializeFromSections(sections);
    return { sections, contentHtml: mat.contentHtml, content: mat.content };
  };

  const proposeAgentPlan = async () => {
    const page = getActiveVisualPage();
    if (!page) return;
    const safePage = ensureSectionsForPage(page);
    try {
      setAgentError("");
      setAgentLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/plan` : "/website/plan";
      const resp = await axios.post(endpoint, {
        websiteName: websiteData?.name || "",
        websiteDescription: websiteData?.description || "",
        page: {
          id: safePage.id,
          title: safePage.title,
          sections: safePage.sections || [],
        },
        goal: agentGoal,
      });
      const summary = typeof resp.data?.summary === "string" ? resp.data.summary : "";
      const operations = Array.isArray(resp.data?.operations) ? resp.data.operations : [];
      setAgentPlan({ summary, operations });
    } catch (e) {
      console.error("Agent plan failed:", e);
      setAgentError(e?.response?.data?.error || "Failed to generate a plan. Please try again.");
    } finally {
      setAgentLoading(false);
    }
  };

  const proposeAgentPlanVariants = async () => {
    const page = getActiveVisualPage();
    if (!page) return;
    const safePage = ensureSectionsForPage(page);
    try {
      setAgentError("");
      setAgentLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/plan-variants` : "/website/plan-variants";
      const resp = await axios.post(endpoint, {
        websiteName: websiteData?.name || "",
        websiteDescription: websiteData?.description || "",
        page: {
          id: safePage.id,
          title: safePage.title,
          sections: safePage.sections || [],
        },
        goal: agentGoal,
      });
      const options = Array.isArray(resp.data?.options) ? resp.data.options : [];
      const byKey = {};
      for (const o of options) {
        if (!o || (o.key !== "A" && o.key !== "B")) continue;
        byKey[o.key] = {
          label: String(o.label || (o.key === "A" ? "Option A (fast/minimal)" : "Option B (premium/detailed)")),
          summary: String(o.summary || ""),
          operations: Array.isArray(o.operations) ? o.operations : [],
        };
      }
      setAgentPlans({
        A: byKey.A || { label: "Option A (fast/minimal)", summary: "", operations: [] },
        B: byKey.B || { label: "Option B (premium/detailed)", summary: "", operations: [] },
      });
      setSelectedAgentPlanKey("A");
      setAgentPlan(null);
    } catch (e) {
      console.error("Agent variants failed:", e);
      setAgentError(e?.response?.data?.error || "Failed to generate A/B plans. Please try again.");
    } finally {
      setAgentLoading(false);
    }
  };

  const applyAgentPlan = () => {
    const page = getActiveVisualPage();
    const planToApply =
      agentPlan ||
      (agentPlans && selectedAgentPlanKey && agentPlans[selectedAgentPlanKey]
        ? { summary: agentPlans[selectedAgentPlanKey].summary, operations: agentPlans[selectedAgentPlanKey].operations }
        : null);
    if (!page || !planToApply) return;
    savePageVersion(page.id, `Agent apply (${selectedAgentPlanKey || "single"}): ${agentGoal}`);
    setWebsiteData((prev) => {
      const next = (() => {
        const id = String(page.id);
        const p = prev.pagesById?.[id];
        if (!p) return prev;
        // Apply operations (same as applyPlanToPage, but pure on prev)
        const ops = planToApply.operations || [];
        let sections = Array.isArray(p.sections) ? p.sections.map((s) => ({ ...s })) : [];
        if (sections.length === 0) {
          const baseHtml = (p.contentHtml && String(p.contentHtml).trim())
            ? String(p.contentHtml)
            : `<p>${String(p.content || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
          sections = [{ id: `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`, type: "paragraph", contentHtml: sanitizeHtmlLight(baseHtml) }];
        }
        const byId = new Map(sections.map((s) => [String(s.id), s]));
        for (const op of ops) {
          const t = String(op?.type || "");
          if (t === "add_section") {
            const sec = op?.section || {};
            const newId = String(sec.id || `sec_${Date.now()}_${Math.random().toString(16).slice(2)}`);
            const newSection = {
              id: newId,
              type: String(sec.type || "paragraph"),
              contentHtml: sanitizeHtmlLight(String(sec.contentHtml || "<p></p>")),
            };
            const afterId = op.afterSectionId ? String(op.afterSectionId) : "";
            if (afterId && byId.has(afterId)) {
              const idx = sections.findIndex((s) => String(s.id) === afterId);
              sections.splice(idx + 1, 0, newSection);
            } else {
              sections.push(newSection);
            }
            byId.set(newId, newSection);
          }
          if (t === "update_section") {
            const sid = String(op.sectionId || "");
            const target = byId.get(sid);
            if (!target) continue;
            const patch = op.patch || {};
            if (patch.type) target.type = String(patch.type);
            if (patch.contentHtml != null) target.contentHtml = sanitizeHtmlLight(String(patch.contentHtml));
          }
          if (t === "delete_section") {
            const sid = String(op.sectionId || "");
            sections = sections.filter((s) => String(s.id) !== sid);
            byId.delete(sid);
          }
          if (t === "reorder_sections") {
            const order = Array.isArray(op.sectionOrder) ? op.sectionOrder.map(String) : [];
            if (order.length > 0) {
              const nextOrder = [];
              const seen = new Set();
              for (const sid of order) {
                const s = byId.get(sid);
                if (s) {
                  nextOrder.push(s);
                  seen.add(sid);
                }
              }
              for (const s of sections) {
                const sid = String(s.id);
                if (!seen.has(sid)) nextOrder.push(s);
              }
              sections = nextOrder;
            }
          }
        }
        const mat = materializeFromSections(sections);
        return {
          ...prev,
          pagesById: {
            ...(prev.pagesById || {}),
            [id]: { ...p, sections, contentHtml: mat.contentHtml, content: mat.content },
          },
        };
      })();
      // Refresh preview using the computed next state
      try {
        setVisualPreview(buildVisualEditorPreview(getOrderedPages(next), audienceSegment));
      } catch (_) {}
      return next;
    });
  };

  const runAiAssist = async (goal) => {
    const page = getActiveVisualPage();
    if (!page) return;
    try {
      setAiAssistError("");
      setAiAssistLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/website/assist` : "/website/assist";
      const resp = await axios.post(endpoint, {
        websiteName: websiteData?.name || "",
        websiteDescription: websiteData?.description || "",
        page: {
          id: page.id,
          title: page.title,
          content: page.content || "",
          contentHtml: page.contentHtml || "",
        },
        goal,
      });
      const contentHtml = typeof resp.data?.contentHtml === "string" ? resp.data.contentHtml : "";
      const contentPlain = typeof resp.data?.contentPlain === "string" ? resp.data.contentPlain : "";
      const metaDescription = typeof resp.data?.metaDescription === "string" ? resp.data.metaDescription : "";

      updatePage(page.id, { contentHtml, content: contentPlain, metaDescription });
      // Refresh the preview so the iframe reflects the new HTML immediately.
      setVisualPreview(
        buildVisualEditorPreview(
          getOrderedPages({
            ...websiteData,
            pagesById: {
              ...(websiteData.pagesById || {}),
              [String(page.id)]: {
                ...(websiteData.pagesById || {})[String(page.id)],
                id: String(page.id),
                contentHtml,
                content: contentPlain,
                metaDescription,
              },
            },
          })
        )
      );
    } catch (e) {
      console.error("AI assist failed:", e);
      setAiAssistError(e?.response?.data?.error || "AI assist failed. Please try again.");
    } finally {
      setAiAssistLoading(false);
    }
  };

  const savePageVersion = (pageId, note = "") => {
    const id = String(pageId || "");
    if (!id) return;
    setWebsiteData((prev) => {
      const page = prev.pagesById?.[id];
      if (!page) return prev;
      const v = {
        id: `ver_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        ts: new Date().toISOString(),
        note: String(note || "").trim(),
        snapshot: {
          title: page.title || "",
          content: page.content || "",
          contentHtml: page.contentHtml || "",
          sections: Array.isArray(page.sections) ? page.sections : [],
          visible: page.visible !== false,
          metaDescription: page.metaDescription || "",
        },
      };
      const versions = Array.isArray(page.versions) ? page.versions : [];
      return {
        ...prev,
        pagesById: {
          ...(prev.pagesById || {}),
          [id]: { ...page, versions: [v, ...versions].slice(0, 25) },
        },
      };
    });
  };

  const restorePageVersion = (pageId, versionId) => {
    const id = String(pageId || "");
    const vid = String(versionId || "");
    if (!id || !vid) return;
    setWebsiteData((prev) => {
      const page = prev.pagesById?.[id];
      if (!page) return prev;
      const versions = Array.isArray(page.versions) ? page.versions : [];
      const v = versions.find((x) => String(x?.id) === vid);
      if (!v || !v.snapshot) return prev;
      const snap = v.snapshot;
      return {
        ...prev,
        pagesById: {
          ...(prev.pagesById || {}),
          [id]: {
            ...page,
            title: String(snap.title || ""),
            content: String(snap.content || ""),
            contentHtml: String(snap.contentHtml || ""),
            sections: Array.isArray(snap.sections) ? snap.sections : [],
            visible: snap.visible !== false,
            metaDescription: String(snap.metaDescription || ""),
          },
        },
      };
    });
  };

  const duplicatePageFromVersion = (pageId, versionId) => {
    const page = (websiteData?.pagesById || {})[String(pageId || "")];
    const versions = Array.isArray(page?.versions) ? page.versions : [];
    const v = versions.find((x) => String(x?.id) === String(versionId || ""));
    if (!v?.snapshot) return;
    addPage({
      title: `${String(v.snapshot.title || "Untitled")} (Draft)`,
      content: v.snapshot.content || "",
      contentHtml: v.snapshot.contentHtml || "",
      visible: v.snapshot.visible !== false,
      sections: Array.isArray(v.snapshot.sections) ? v.snapshot.sections : [],
      metaDescription: v.snapshot.metaDescription || "",
      versions: [],
    });
  };

  const buildStandaloneHtml = () => {
    if (generatedHtmlDoc) return generatedHtmlDoc;
    return buildStandaloneHtmlFromParts({
      html: generatedArtifacts?.html || "",
      css: generatedArtifacts?.css || "",
      js: generatedArtifacts?.js || "",
      title: websiteData?.name || "Website",
    });
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch (e) {
      console.error("Copy failed", e);
      setError("Copy failed. Your browser may block clipboard access.");
    }
  };

  const downloadTextFile = (filename, content, mime = "text/plain;charset=utf-8") => {
    try {
      const blob = new Blob([content || ""], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed", e);
      setError("Download failed.");
    }
  };

  return (
    <div className="website-builder-container">
      <div className="website-builder-header">
        <h1>ðŸŒ Website Builder</h1>
        <p>Create your website with AI-powered design and integrated login & payments</p>
      </div>

      {error && (
        <div className="website-builder-error">
          {error}
          <button onClick={() => setError(null)}>Ã—</button>
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div className="website-builder-modal">
          <div className="website-builder-modal-content">
            <h2>Login Required</h2>
            <p>Please login to continue building your website.</p>
            <div className="login-options">
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin
                  onSuccess={handleGoogleCredential}
                  onError={() => setError("Google login failed. Please try again.")}
                />
              </div>
            </div>
            <button
              onClick={() => setShowLogin(false)}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (
        <div className="website-builder-modal">
          <div className="website-builder-modal-content">
            <h2>Upgrade Required</h2>
            <p>Website creation with login and payment integration requires a Pro plan or higher.</p>
            <div className="payment-options">
              <button
                onClick={() => handleStripeCheckout("pro")}
                disabled={loading}
                className="payment-btn"
              >
                Upgrade to Pro - $20/month
              </button>
              <button
                onClick={() => handleStripeCheckout("ultra")}
                disabled={loading}
                className="payment-btn"
              >
                Upgrade to Ultra - $200/month
              </button>
            </div>
            <button
              onClick={() => setShowPayment(false)}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="website-builder-step">
          <h2>Step 1: Basic Information</h2>
          
          <div className="integration-section">
            <h3>ðŸ§© Tech Stack</h3>
            <div className="form-group">
              <label>What do you want to build with?</label>
              <select
                value={websiteData.techStack}
                onChange={(e) => setWebsiteData({ ...websiteData, techStack: e.target.value })}
              >
                <optgroup label="Web (runnable preview)">
                  <option value="html">HTML + CSS + JavaScript â€” Fastest, always runnable.</option>
                  <option value="react">React â€” Component-based UI, great for apps.</option>
                  <option value="vue">Vue 3 (CDN) â€” Lightweight Vue without build step.</option>
                  <option value="alpine">Alpine.js (CDN) â€” Minimal JS for interactive sites.</option>
                </optgroup>
                <optgroup label="Coming soon">
                  <option value="next">Next.js (beta) â€” Generates a React preview for now.</option>
                  <option value="svelte">Svelte (beta) â€” Generates an HTML preview for now.</option>
                  <option value="angular">Angular (beta) â€” Generates an HTML preview for now.</option>
                  <option value="flutter">Flutter Web (beta) â€” Generates an HTML preview for now.</option>
                </optgroup>
              </select>
              <small>This changes what code Zolaid generates for you.</small>
            </div>
          </div>

          <div className="integration-section">
            <h3>ðŸ” Login Integration</h3>
            <div className="integration-options">
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.google}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      authProviders: { ...websiteData.authProviders, google: e.target.checked },
                    })
                  }
                />
                Google
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.microsoft}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      authProviders: { ...websiteData.authProviders, microsoft: e.target.checked },
                    })
                  }
                />
                Microsoft
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.apple}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      authProviders: { ...websiteData.authProviders, apple: e.target.checked },
                    })
                  }
                />
                Apple
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.facebook}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      authProviders: { ...websiteData.authProviders, facebook: e.target.checked },
                    })
                  }
                />
                Facebook
              </label>
            </div>
            <small>These will be placeholder integrations (no secrets).</small>
          </div>

          <div className="integration-section">
            <h3>ðŸ’³ Payment Integration</h3>
            <div className="integration-options">
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.payments.enabled}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      payments: { ...websiteData.payments, enabled: e.target.checked },
                      standardPages: {
                        ...websiteData.standardPages,
                        refund: e.target.checked ? true : false,
                      },
                    })
                  }
                />
                Enable payments
              </label>
            </div>
            <div className="form-group">
              <label>Provider</label>
              <select
                value={websiteData.payments.provider}
                onChange={(e) =>
                  setWebsiteData({
                    ...websiteData,
                    payments: { ...websiteData.payments, provider: e.target.value },
                  })
                }
                disabled={!websiteData.payments.enabled}
              >
                <option value="stripe">Stripe</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>

          <div className="integration-section">
            <h3>ðŸ”§ Integration Mode</h3>
            <div className="form-group">
              <label>How should Zolaid generate integrations?</label>
              <select
                value={websiteData.integrationMode}
                onChange={(e) => setWebsiteData({ ...websiteData, integrationMode: e.target.value })}
              >
                <option value="demo">Demo placeholders (no real auth/payments)</option>
                <option value="env">I have keys (use env vars + wiring + stubs)</option>
              </select>
              <small>
                Demo mode is safest. â€œI have keysâ€ mode generates env-var wiring and adds helpful stubs/instructions.
              </small>
            </div>
          </div>

          <div className="form-group">
            <label>Website Name</label>
            <input
              type="text"
              value={websiteData.name}
              onChange={(e) => setWebsiteData({ ...websiteData, name: e.target.value })}
              placeholder="My Awesome Website"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={websiteData.description}
              onChange={(e) => setWebsiteData({ ...websiteData, description: e.target.value })}
              placeholder="Describe what your website is about..."
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>Domain (optional)</label>
            <input
              type="text"
              value={websiteData.domain}
              onChange={(e) => setWebsiteData({ ...websiteData, domain: e.target.value })}
              placeholder="mywebsite.com"
            />
          </div>
          <button
            onClick={() => setStep(2)}
            disabled={!websiteData.name.trim()}
            className="next-btn"
          >
            Next: Standard Pages
          </button>
        </div>
      )}

      {/* Step 2: Standard Business Pages */}
      {step === 2 && (
        <div className="website-builder-step">
          <h2>Step 2: Standard Business Pages</h2>
          <div className="integration-section">
            <h3>Do you want standard business pages generated?</h3>
            <div className="integration-options" style={{ flexDirection: "column", alignItems: "flex-start" }}>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.about}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, about: e.target.checked },
                    })
                  }
                />
                About Us
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.team}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, team: e.target.checked },
                    })
                  }
                />
                Team
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.careers}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, careers: e.target.checked },
                    })
                  }
                />
                Careers
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.apps}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, apps: e.target.checked },
                    })
                  }
                />
                Apps
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.contact}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, contact: e.target.checked },
                    })
                  }
                />
                Contact Us
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.privacy}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, privacy: e.target.checked },
                    })
                  }
                />
                Privacy Policy
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.terms}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, terms: e.target.checked },
                    })
                  }
                />
                Terms of Service
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.standardPages.cookie}
                  onChange={(e) =>
                    setWebsiteData({
                      ...websiteData,
                      standardPages: { ...websiteData.standardPages, cookie: e.target.checked },
                    })
                  }
                />
                Cookie Policy
              </label>
              {websiteData.payments.enabled && (
                <label>
                  <input
                    type="checkbox"
                    checked={websiteData.standardPages.refund}
                    onChange={(e) =>
                      setWebsiteData({
                        ...websiteData,
                        standardPages: { ...websiteData.standardPages, refund: e.target.checked },
                      })
                    }
                  />
                  Refund Policy
                </label>
              )}
            </div>
            <small>
              Weâ€™ll create these pages automatically. You can still add/edit pages in the next step.
            </small>
          </div>

          <div className="page-actions">
            <button
              type="button"
              className="save-btn"
              onClick={() => {
                addStandardPagesToWebsiteData();
                setStep(3);
              }}
            >
              Continue
            </button>
            <button type="button" className="cancel-btn" onClick={() => setStep(1)}>
              Back
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Add Pages */}
      {step === 3 && (
        <div className="website-builder-step">
          <h2>Step 3: Create Pages</h2>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "10px" }}>
            <button
              type="button"
              className="generate-btn"
              onClick={() => {
                setShowVisualEditor((v) => {
                  const next = !v;
                  if (next) {
                    setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), audienceSegment));
                  }
                  return next;
                });
              }}
            >
              {showVisualEditor ? "Close Visual Editor" : "Open Visual Editor (beta)"}
            </button>
            <button type="button" className="generate-btn" onClick={saveTemplate}>
              Save as template
            </button>
            <button type="button" className="generate-btn" onClick={publishCommunityTemplate}>
              Publish template
            </button>
            <button type="button" className="generate-btn" onClick={updatePublishedCommunityTemplate} disabled={!lastPublishedTemplateId}>
              Update published
            </button>
            <button type="button" className="generate-btn" onClick={copyPublishedTemplateShareLink} disabled={!lastPublishedTemplateId}>
              Copy share link
            </button>
            <button type="button" className="generate-btn" onClick={openPublishedTemplateInTemplates} disabled={!lastPublishedTemplateId}>
              Open in Templates
            </button>
            <button type="button" className="cancel-btn" onClick={deletePublishedCommunityTemplate} disabled={!lastPublishedTemplateId}>
              Delete published
            </button>
            <button type="button" className="generate-btn" onClick={saveProjectToBackend}>
              Save project
            </button>
            <button type="button" className="generate-btn" onClick={saveAsNewProjectToBackend}>
              Save as new
            </button>
            <button type="button" className="generate-btn" onClick={saveProjectVersion} disabled={!currentProjectId}>
              Snapshot
            </button>
            <button type="button" className="generate-btn" onClick={refreshProjectVersions} disabled={!currentProjectId || projectVersionsLoading}>
              {projectVersionsLoading ? "Loadingâ€¦" : "Versions"}
            </button>
            <button type="button" className="generate-btn" onClick={copyBuilderLinkForCurrentProject} disabled={!currentProjectId}>
              Copy builder link
            </button>
            <button type="button" className="generate-btn" onClick={openBuilderLinkForCurrentProject} disabled={!currentProjectId}>
              Open builder link
            </button>
            <button type="button" className="generate-btn" onClick={sendCurrentToComputerPreview}>
              Send to Preview
            </button>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: "#cccccc",
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.12)",
              }}
              title={typeof onSendToComputerPreview === "function" ? "Auto-send preview snapshots while you edit (debounced)" : "Only available inside Zolaid Computer"}
            >
              <input
                type="checkbox"
                checked={computerLivePreview}
                onChange={(e) => setComputerLivePreview(e.target.checked)}
                disabled={typeof onSendToComputerPreview !== "function"}
              />
              Live preview
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#cccccc" }}>
              <input
                type="checkbox"
                checked={autosaveEnabled}
                onChange={(e) => setAutosaveEnabled(e.target.checked)}
                disabled={!currentProjectId}
              />
              Autosave
            </label>
            {showVisualEditor && (
              <button
                type="button"
                className="generate-btn"
                onClick={() => setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), audienceSegment))}
              >
                Refresh from pages
              </button>
            )}
            <span style={{ fontSize: "12px", color: "#cccccc" }}>
              Visual editor edits sync back into your pages automatically.
            </span>
          </div>

          {showVisualEditor && currentProjectId && projectVersionsError && (
            <div style={{ marginBottom: "10px", fontSize: "12px", color: "#ffb4b4" }}>{projectVersionsError}</div>
          )}
          {showVisualEditor && currentProjectId && Array.isArray(projectVersions) && projectVersions.length > 0 && (
            <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 900 }}>Project Versions</div>
                  <div style={{ fontSize: "12px", color: "#cccccc" }}>
                    Snapshots of the whole project (prototype).
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {projectVersions.slice(0, 8).map((v) => (
                  <div key={v.id} style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900 }}>{String(v.label || "Snapshot")}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>{v.ts ? new Date(v.ts).toLocaleString() : ""}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button className="generate-btn" type="button" onClick={() => restoreProjectVersion(v.id)}>
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showVisualEditor && (
            <>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ fontWeight: 800, color: "#ffffff" }}>Device preview</div>
                <button className="generate-btn" type="button" onClick={() => setVisualDevice("desktop")}>
                  Desktop
                </button>
                <button className="generate-btn" type="button" onClick={() => setVisualDevice("tablet")}>
                  Tablet
                </button>
                <button className="generate-btn" type="button" onClick={() => setVisualDevice("mobile")}>
                  Mobile
                </button>
                <div style={{ width: "1px", height: "18px", background: "rgba(255,255,255,0.18)", margin: "0 6px" }} />
                <div style={{ fontWeight: 800, color: "#ffffff" }}>Audience segment</div>
                <select
                  value={audienceSegment}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAudienceSegment(next);
                    setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), next));
                  }}
                  style={{
                    padding: "8px 10px",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(0,0,0,0.18)",
                    color: "#ffffff",
                  }}
                >
                  <option value="default">Default</option>
                  <option value="new">New visitors</option>
                  <option value="returning">Returning</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Brand Kit (Prototype)</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Shared style preferences to keep website outputs consistent (colors, tone, font).
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#cccccc", fontWeight: 800, marginBottom: 6 }}>Tone</div>
                    <select
                      value={websiteData?.brand?.tone || "modern"}
                      onChange={(e) => setWebsiteData((prev) => ({ ...prev, brand: { ...(prev.brand || {}), tone: e.target.value } }))}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", color: "#fff" }}
                    >
                      <option value="modern">Modern</option>
                      <option value="friendly">Friendly</option>
                      <option value="premium">Premium</option>
                      <option value="bold">Bold</option>
                      <option value="minimal">Minimal</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#cccccc", fontWeight: 800, marginBottom: 6 }}>Primary color</div>
                    <input
                      type="text"
                      value={websiteData?.brand?.primaryColor || "#7c3aed"}
                      onChange={(e) => setWebsiteData((prev) => ({ ...prev, brand: { ...(prev.brand || {}), primaryColor: e.target.value } }))}
                      placeholder="#7c3aed"
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", color: "#fff" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#cccccc", fontWeight: 800, marginBottom: 6 }}>Secondary color</div>
                    <input
                      type="text"
                      value={websiteData?.brand?.secondaryColor || "#2563eb"}
                      onChange={(e) => setWebsiteData((prev) => ({ ...prev, brand: { ...(prev.brand || {}), secondaryColor: e.target.value } }))}
                      placeholder="#2563eb"
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", color: "#fff" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#cccccc", fontWeight: 800, marginBottom: 6 }}>Font family</div>
                    <input
                      type="text"
                      value={websiteData?.brand?.fontFamily || "system-ui"}
                      onChange={(e) => setWebsiteData((prev) => ({ ...prev, brand: { ...(prev.brand || {}), fontFamily: e.target.value } }))}
                      placeholder='system-ui, "Inter", "Poppins"'
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", color: "#fff" }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                  Tip: after changing the brand kit, regenerate the website to see the AI apply it more strongly.
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button className="generate-btn" type="button" disabled={heroImageLoading} onClick={suggestHeroImage}>
                    {heroImageLoading ? "Generating hero image..." : "Suggest hero image"}
                  </button>
                  {heroImageError && (
                    <span style={{ fontSize: 12, color: "#ffb4b4" }}>{heroImageError}</span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Asset Library (Prototype)</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Browse uploaded/generated assets and insert into the current page.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="generate-btn" type="button" disabled={assetLibraryLoading} onClick={refreshAssetLibrary}>
                      {assetLibraryLoading ? "Loading..." : "Refresh"}
                    </button>
                  </div>
                </div>
                {assetLibraryError && <div style={{ marginTop: 8, fontSize: 12, color: "#ffb4b4" }}>{assetLibraryError}</div>}
                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
                  {(assetLibrary || []).slice(0, 12).map((a) => (
                    <div key={String(a.filename || a.url)} style={{ padding: 8, borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                      <div style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: 10, overflow: "hidden", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <img
                          src={(process.env.REACT_APP_API_URL || "") && String(a.url || "").startsWith("/") ? `${(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")}${String(a.url || "")}` : String(a.url || "")}
                          alt={String(a.filename || "asset")}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                      <button className="generate-btn" type="button" style={{ width: "100%", marginTop: 8 }} onClick={() => insertAssetAsImageSection(a.url)}>
                        Insert
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                  Tip: assets come from <code>/uploads/site-assets</code> (uploads + generated hero images).
                </div>
              </div>
              {(() => {
                const p = getActiveVisualPage();
                if (!p) return null;
                const versions = Array.isArray(p.versions) ? p.versions : [];
                return (
                  <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>Versions</div>
                        <div style={{ fontSize: "12px", color: "#cccccc" }}>
                          Save snapshots, rollback, or branch drafts (per page).
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                          className="generate-btn"
                          type="button"
                          onClick={() => {
                            const note = window.prompt("Version note (optional):", "");
                            savePageVersion(p.id, note || "");
                          }}
                        >
                          Save version
                        </button>
                        <button
                          className="generate-btn"
                          type="button"
                          onClick={() => {
                            // Refresh the preview (useful after restore)
                            setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), audienceSegment));
                          }}
                        >
                          Refresh preview
                        </button>
                      </div>
                    </div>

                    {versions.length === 0 ? (
                      <div style={{ marginTop: "8px", fontSize: "12px", color: "#cccccc" }}>
                        No versions yet.
                      </div>
                    ) : (
                      <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
                        {versions.slice(0, 6).map((v) => (
                          <div
                            key={v.id}
                            style={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 10px",
                              borderRadius: "12px",
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {v.note ? v.note : "Version"} â€¢ {new Date(v.ts).toLocaleString()}
                              </div>
                              <div style={{ fontSize: "12px", color: "#cccccc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {String(v.snapshot?.title || p.title || "Untitled")}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <button
                                className="generate-btn"
                                type="button"
                                onClick={() => {
                                  restorePageVersion(p.id, v.id);
                                  setTimeout(() => setVisualPreview(buildVisualEditorPreview(getOrderedPages(websiteData), audienceSegment)), 0);
                                }}
                              >
                                Restore
                              </button>
                              <button
                                className="generate-btn"
                                type="button"
                                onClick={() => duplicatePageFromVersion(p.id, v.id)}
                              >
                                Duplicate draft
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Autonomous Agent (Plan â†’ Apply)</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      The agent proposes a safe edit plan for the current page. You review, then apply (with rollback).
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button className="generate-btn" type="button" disabled={agentLoading} onClick={proposeAgentPlan}>
                      {agentLoading ? "Planning..." : "Propose plan"}
                    </button>
                    <button className="generate-btn" type="button" disabled={agentLoading} onClick={proposeAgentPlanVariants}>
                      Propose A/B
                    </button>
                    <button className="generate-btn" type="button" disabled={agentLoading} onClick={autoOptimizeFromKpis}>
                      Auto-optimize from KPIs
                    </button>
                    <button className="generate-btn" type="button" disabled={kpiGoalLoading || !previewSessionId} onClick={suggestGoalFromKpis}>
                      {kpiGoalLoading ? "Suggesting..." : "Suggest goal from KPIs"}
                    </button>
                    <button className="generate-btn" type="button" disabled={kpiGoalLoading || !currentProjectId} onClick={suggestGoalFromProjectKpis}>
                      Suggest from Project KPIs
                    </button>
                    <button className="generate-btn" type="button" disabled={!agentPlan || agentLoading} onClick={applyAgentPlan}>
                      Apply plan
                    </button>
                    <button className="generate-btn" type="button" disabled={agentLoading} onClick={() => setAgentPlan(null)}>
                      Clear
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap", fontSize: "12px", color: "#cccccc" }}>
                  <span><strong>KPIs</strong></span>
                  <span>Clicks: {Number(kpis.clicks || 0)}</span>
                  <span>Max scroll: {Number(kpis.maxScrollPct || 0)}%</span>
                  {previewSessionId ? <span>Session: {String(previewSessionId).slice(0, 16)}â€¦</span> : <span>Session: (waiting)</span>}
                  {currentProjectId ? <span>Project: {String(currentProjectId).slice(0, 12)}â€¦</span> : <span>Project: (none)</span>}
                  <button className="generate-btn" type="button" disabled={!currentProjectId || projectKpisLoading} onClick={refreshProjectKpis}>
                    {projectKpisLoading ? "Loading project KPIsâ€¦" : "Project KPIs"}
                  </button>
                  <button className="cancel-btn" type="button" disabled={!currentProjectId || projectKpisLoading} onClick={resetProjectKpis}>
                    Reset
                  </button>
                </div>
                {kpiGoalError && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#ffb4b4" }}>{kpiGoalError}</div>
                )}
                {projectKpisError && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#ffb4b4" }}>{projectKpisError}</div>
                )}
                {projectKpis?.counters && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#cccccc" }}>
                    <strong>Project KPIs</strong>: clicks={Number(projectKpis.counters.clicks || 0)}, maxScroll={Number(projectKpis.counters.maxScrollPct || 0)}%, sessions={Number(projectKpis.recentSessionCount || 0)}
                  </div>
                )}
                {currentProjectId && (
                  <div style={{ marginTop: "10px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }}>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>Scheduled Autoâ€‘Optimize (Prototype)</div>
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                          Generates KPI-based goal recommendations on a schedule (project-level).
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button className="generate-btn" type="button" disabled={autoOptLoading} onClick={refreshAutoOptimizeSchedule}>
                          {autoOptLoading ? "Loading..." : "Refresh"}
                        </button>
                        <button className="generate-btn" type="button" disabled={autoOptLoading} onClick={runAutoOptimizeNow}>
                          Run now
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#cccccc" }}>
                        <input
                          type="checkbox"
                          checked={autoOptEnabled}
                          onChange={(e) => setAutoOptimizeSchedule(e.target.checked, autoOptIntervalMin)}
                        />
                        Enable
                      </label>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#cccccc" }}>
                        Interval (min)
                        <select
                          value={autoOptIntervalMin}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            setAutoOptIntervalMin(next);
                            setAutoOptimizeSchedule(autoOptEnabled, next);
                          }}
                          style={{ padding: "6px 10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", color: "#fff" }}
                        >
                          <option value={15}>15</option>
                          <option value={30}>30</option>
                          <option value={60}>60</option>
                          <option value={180}>180</option>
                          <option value={1440}>1440</option>
                        </select>
                      </label>
                    </div>
                    {autoOptError && <div style={{ marginTop: 8, fontSize: 12, color: "#ffb4b4" }}>{autoOptError}</div>}
                    {Array.isArray(autoOptHistory) && autoOptHistory.length > 0 && (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                        {autoOptHistory.slice(0, 3).map((h, idx) => (
                          <div key={`aoh_${idx}_${h.ts || idx}`} style={{ padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                              {h.ts ? new Date(h.ts).toLocaleString() : ""} â€¢ {String(h.mode || "")}
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.90)" }}>{String(h.goal || "")}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: "10px" }}>
                  <div style={{ fontSize: "12px", fontWeight: 800, color: "#cccccc", marginBottom: "6px" }}>Goal</div>
                  <input
                    type="text"
                    value={agentGoal}
                    onChange={(e) => setAgentGoal(e.target.value)}
                    placeholder="e.g., Make this page persuasive for sales with a clear CTA."
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(0,0,0,0.18)",
                      color: "#ffffff",
                      outline: "none",
                    }}
                  />
                  {agentError && (
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "#ffb4b4" }}>{agentError}</div>
                  )}
                </div>

                {agentPlan && (
                  <div style={{ marginTop: "10px" }}>
                    {agentPlan.summary && (
                      <div style={{ fontSize: "12px", color: "#cccccc", marginBottom: "8px" }}>
                        <span style={{ fontWeight: 900 }}>Summary:</span> {agentPlan.summary}
                      </div>
                    )}
                    <div style={{ fontWeight: 900, marginBottom: "6px" }}>Proposed changes</div>
                    <div style={{ fontSize: "12px", color: "#cccccc", whiteSpace: "pre-wrap" }}>
                      {(agentPlan.operations || []).map((op, idx) => {
                        const t = String(op?.type || "op");
                        if (t === "add_section") return `+ Add section (${op?.section?.type || "paragraph"})`;
                        if (t === "update_section") return `~ Update section ${String(op.sectionId || "").slice(0, 8)}â€¦`;
                        if (t === "delete_section") return `- Delete section ${String(op.sectionId || "").slice(0, 8)}â€¦`;
                        if (t === "reorder_sections") return `â†• Reorder sections`;
                        return `â€¢ ${t}`;
                      }).join("\n")}
                    </div>
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                      <button className="generate-btn" type="button" onClick={() => setShowAgentPreview((v) => !v)}>
                        {showAgentPreview ? "Hide preview" : "Show preview"}
                      </button>
                      <span style={{ fontSize: "12px", color: "#cccccc" }}>Preview shows current vs proposed sections.</span>
                    </div>

                    {showAgentPreview && (() => {
                      const p = getActiveVisualPage();
                      if (!p) return null;
                      const currentSections = Array.isArray(p.sections) ? p.sections : [];
                      const simulated = simulatePlanForPage(p, agentPlan);
                      const proposedSections = Array.isArray(simulated.sections) ? simulated.sections : [];
                      const renderSection = (s) => {
                        const html = sanitizeHtmlLight(String(s?.contentHtml || ""));
                        return (
                          <div
                            key={String(s?.id || Math.random())}
                            style={{
                              padding: "8px 10px",
                              borderRadius: "12px",
                              border: "1px solid rgba(255,255,255,0.10)",
                              background: "rgba(255,255,255,0.03)",
                              marginBottom: "8px",
                            }}
                          >
                            <div style={{ fontSize: "11px", color: "#cccccc", marginBottom: "6px" }}>
                              {String(s?.type || "section")} â€¢ {String(s?.id || "").slice(0, 8)}â€¦
                            </div>
                            <div
                              style={{ fontSize: "13px", color: "rgba(255,255,255,0.90)" }}
                              dangerouslySetInnerHTML={{ __html: html || "<p></p>" }}
                            />
                          </div>
                        );
                      };
                      return (
                        <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                          <div>
                            <div style={{ fontWeight: 900, marginBottom: "6px" }}>Current</div>
                            <div style={{ maxHeight: "260px", overflow: "auto", paddingRight: "6px" }}>
                              {currentSections.length === 0 ? (
                                <div style={{ fontSize: "12px", color: "#cccccc" }}>No sections yet.</div>
                              ) : (
                                currentSections.map(renderSection)
                              )}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 900, marginBottom: "6px" }}>Proposed</div>
                            <div style={{ maxHeight: "260px", overflow: "auto", paddingRight: "6px" }}>
                              {proposedSections.length === 0 ? (
                                <div style={{ fontSize: "12px", color: "#cccccc" }}>No sections after plan.</div>
                              ) : (
                                proposedSections.map(renderSection)
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "#cccccc" }}>
                      Applying will auto-save a version snapshot first.
                    </div>
                  </div>
                )}

                {agentPlans && (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "10px" }}>
                      <button className="generate-btn" type="button" onClick={() => setSelectedAgentPlanKey("A")}>
                        {agentPlans.A?.label || "Option A"}
                      </button>
                      <button className="generate-btn" type="button" onClick={() => setSelectedAgentPlanKey("B")}>
                        {agentPlans.B?.label || "Option B"}
                      </button>
                      <button className="generate-btn" type="button" disabled={agentLoading} onClick={applyAgentPlan}>
                        Apply selected ({selectedAgentPlanKey})
                      </button>
                      <button className="generate-btn" type="button" onClick={() => setAgentPlans(null)}>
                        Clear A/B
                      </button>
                    </div>

                    {(() => {
                      const selected = selectedAgentPlanKey === "B" ? agentPlans.B : agentPlans.A;
                      const planObj = { summary: selected?.summary || "", operations: Array.isArray(selected?.operations) ? selected.operations : [] };
                      const p = getActiveVisualPage();
                      if (!p) return null;
                      const simulated = simulatePlanForPage(p, planObj);
                      const currentSections = Array.isArray(p.sections) ? p.sections : [];
                      const proposedSections = Array.isArray(simulated.sections) ? simulated.sections : [];
                      const renderSection = (s) => {
                        const html = sanitizeHtmlLight(String(s?.contentHtml || ""));
                        return (
                          <div
                            key={String(s?.id || Math.random())}
                            style={{
                              padding: "8px 10px",
                              borderRadius: "12px",
                              border: "1px solid rgba(255,255,255,0.10)",
                              background: "rgba(255,255,255,0.03)",
                              marginBottom: "8px",
                            }}
                          >
                            <div style={{ fontSize: "11px", color: "#cccccc", marginBottom: "6px" }}>
                              {String(s?.type || "section")} â€¢ {String(s?.id || "").slice(0, 8)}â€¦
                            </div>
                            <div
                              style={{ fontSize: "13px", color: "rgba(255,255,255,0.90)" }}
                              dangerouslySetInnerHTML={{ __html: html || "<p></p>" }}
                            />
                          </div>
                        );
                      };
                      return (
                        <>
                          {planObj.summary && (
                            <div style={{ fontSize: "12px", color: "#cccccc", marginBottom: "8px" }}>
                              <span style={{ fontWeight: 900 }}>Summary:</span> {planObj.summary}
                            </div>
                          )}
                          <div style={{ marginTop: "6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                            <div>
                              <div style={{ fontWeight: 900, marginBottom: "6px" }}>Current</div>
                              <div style={{ maxHeight: "220px", overflow: "auto", paddingRight: "6px" }}>
                                {currentSections.length === 0 ? (
                                  <div style={{ fontSize: "12px", color: "#cccccc" }}>No sections yet.</div>
                                ) : (
                                  currentSections.map(renderSection)
                                )}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 900, marginBottom: "6px" }}>Proposed ({selectedAgentPlanKey})</div>
                              <div style={{ maxHeight: "220px", overflow: "auto", paddingRight: "6px" }}>
                                {proposedSections.length === 0 ? (
                                  <div style={{ fontSize: "12px", color: "#cccccc" }}>No sections after plan.</div>
                                ) : (
                                  proposedSections.map(renderSection)
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Compliance & Safety</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Checks for missing legal pages and basic HTML safety/accessibility issues.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button className="generate-btn" type="button" disabled={complianceLoading} onClick={runComplianceCheck}>
                      {complianceLoading ? "Checking..." : "Run check"}
                    </button>
                  </div>
                </div>
                {complianceError && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#ffb4b4" }}>{complianceError}</div>
                )}
                {complianceResult && (
                  <div style={{ marginTop: "10px", fontSize: "12px", color: "#cccccc" }}>
                    {complianceResult.errors?.length > 0 && (
                      <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontWeight: 900, color: "#ffb4b4", marginBottom: "6px" }}>Errors</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {complianceResult.errors.map((m) => <li key={`ce_${m}`}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                    {complianceResult.warnings?.length > 0 ? (
                      <div>
                        <div style={{ fontWeight: 900, color: "#ffe7a3", marginBottom: "6px" }}>Warnings</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {complianceResult.warnings.map((m) => <li key={`cw_${m}`}>{m}</li>)}
                        </ul>
                      </div>
                    ) : (
                      <div style={{ color: "rgba(255,255,255,0.72)" }}>No warnings.</div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Emotion & Engagement</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Scores clarity, persuasion, trust, scannability, and emotion. Generates actionable suggestions.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button className="generate-btn" type="button" disabled={emotionLoading} onClick={runEmotionScore}>
                      {emotionLoading ? "Scoring..." : "Score this page"}
                    </button>
                    {emotionResult?.recommendedGoal && (
                      <button
                        className="generate-btn"
                        type="button"
                        onClick={() => {
                          setAgentGoal(String(emotionResult.recommendedGoal || ""));
                          proposeAgentPlanVariants();
                        }}
                      >
                        Use recommended goal
                      </button>
                    )}
                  </div>
                </div>
                {emotionError && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#ffb4b4" }}>{emotionError}</div>
                )}
                {emotionResult && (
                  <div style={{ marginTop: "10px", fontSize: "12px", color: "#cccccc" }}>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <span><strong>Clarity</strong>: {emotionResult?.scores?.clarity ?? 0}</span>
                      <span><strong>Persuasion</strong>: {emotionResult?.scores?.persuasion ?? 0}</span>
                      <span><strong>Trust</strong>: {emotionResult?.scores?.trust ?? 0}</span>
                      <span><strong>Scannability</strong>: {emotionResult?.scores?.scannability ?? 0}</span>
                      <span><strong>Emotion</strong>: {emotionResult?.scores?.emotion ?? 0}</span>
                    </div>
                    {emotionResult?.tone?.primary && (
                      <div style={{ marginTop: "8px" }}>
                        <strong>Tone</strong>: {emotionResult.tone.primary}
                        {Array.isArray(emotionResult.tone.adjectives) && emotionResult.tone.adjectives.length > 0
                          ? ` (${emotionResult.tone.adjectives.join(", ")})`
                          : ""}
                      </div>
                    )}
                    {Array.isArray(emotionResult?.risks) && emotionResult.risks.length > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ fontWeight: 900, color: "#ffe7a3", marginBottom: "6px" }}>Risks</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {emotionResult.risks.map((m) => <li key={`er_${m}`}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(emotionResult?.suggestions) && emotionResult.suggestions.length > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ fontWeight: 900, color: "#ffffff", marginBottom: "6px" }}>Suggestions</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {emotionResult.suggestions.map((m) => <li key={`es_${m}`}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Collaboration (Prototype)</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Real-time syncing across multiple tabs in the same browser (no accounts/servers yet).
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#cccccc" }}>
                      <input
                        type="checkbox"
                        checked={collabEnabled}
                        onChange={(e) => setCollabEnabled(e.target.checked)}
                      />
                      Enable
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#cccccc" }}>
                      <input
                        type="checkbox"
                        checked={cloudCollabEnabled}
                        onChange={(e) => setCloudCollabEnabled(e.target.checked)}
                        disabled={!currentProjectId}
                      />
                      Cloud sync
                    </label>
                  </div>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                  Open Zolaid in another tab and enable this toggle there to see edits sync.
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                  Cloud sync uses your <strong>current project</strong>. {currentProjectId ? `Project: ${String(currentProjectId).slice(0, 16)}â€¦` : "Save or load a project to enable it."}
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ fontWeight: 800, color: "#ffffff" }}>AI Assist</div>
                <span style={{ fontSize: "12px", color: "#cccccc" }}>
                  Applies to the currently selected page in the preview.
                </span>
                {aiAssistError && (
                  <span style={{ fontSize: "12px", color: "#ffb4b4" }}>{aiAssistError}</span>
                )}
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                <button className="generate-btn" type="button" disabled={aiAssistLoading} onClick={() => runAiAssist("Make this page persuasive for sales. Add a clear CTA. Keep it scannable.")}>
                  Persuasive (sales)
                </button>
                <button className="generate-btn" type="button" disabled={aiAssistLoading} onClick={() => runAiAssist("Simplify this page for mobile readers. Short sentences, clear headings, concise bullets.")}>
                  Mobile-friendly
                </button>
                <button className="generate-btn" type="button" disabled={aiAssistLoading} onClick={() => runAiAssist("Rewrite in a more formal, professional tone. Avoid hype.")}>
                  More formal
                </button>
                <button className="generate-btn" type="button" disabled={aiAssistLoading} onClick={() => runAiAssist("Shorten this content by ~30% while keeping meaning.")}>
                  Shorten
                </button>
                <button className="generate-btn" type="button" disabled={aiAssistLoading} onClick={() => runAiAssist("Generate a strong meta description for this page (140-160 chars) and improve clarity.")}>
                  Meta description
                </button>
              </div>
              <div style={{ height: "520px", marginBottom: "14px" }}>
                <LivePreview
                  html={visualPreview.html}
                  css={visualPreview.css}
                  js={visualPreview.js}
                  isVisible={true}
                  onRuntimeEvent={handlePreviewRuntimeEventWithAnalytics}
                  frameDevice={true}
                  enableAnalytics={true}
                  enableAutomation={true}
                  viewportWidth={
                    visualDevice === "mobile"
                      ? 390
                      : visualDevice === "tablet"
                        ? 820
                        : 1280
                  }
                />
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Automation: Leads</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Form submissions inside the preview are captured as leads (prototype).
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button className="generate-btn" type="button" disabled={!previewSessionId || leadsLoading} onClick={refreshLeads}>
                      {leadsLoading ? "Loading..." : "Refresh"}
                    </button>
                  </div>
                </div>
                {leadsError && <div style={{ marginTop: "8px", fontSize: "12px", color: "#ffb4b4" }}>{leadsError}</div>}
                <div style={{ marginTop: "10px", fontSize: "12px", color: "#cccccc" }}>
                  {!previewSessionId ? (
                    <div>Waiting for preview sessionâ€¦</div>
                  ) : leads.length === 0 ? (
                    <div>No leads yet. Try submitting a form inside the preview.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {leads.slice(0, 8).map((l, idx) => (
                        <div
                          key={`lead_${idx}_${l.ts || l.receivedAt || idx}`}
                          style={{
                            padding: "8px 10px",
                            borderRadius: "12px",
                            border: "1px solid rgba(255,255,255,0.08)",
                            background: "rgba(255,255,255,0.03)",
                          }}
                        >
                          <div style={{ fontWeight: 900 }}>
                            Lead â€¢ {new Date(l.ts || l.receivedAt || Date.now()).toLocaleString()}
                          </div>
                          <div style={{ marginTop: "6px" }}>
                            {l.fields && typeof l.fields === "object" ? (
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {Object.entries(l.fields).slice(0, 6).map(([k, v]) => (
                                  <li key={`${k}`}>
                                    <strong>{k}</strong>: {String(v)}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div>(No fields)</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Impact (Prototype)</div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Simulate macro outcomes/risks/opportunities and turn the summary into an agent goal.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      className="generate-btn"
                      type="button"
                      onClick={() => {
                        if (!impactGoal.trim()) {
                          setImpactGoal(`Grow ${websiteData?.name || "this website"}: improve conversion, trust, and retention.`);
                        }
                      }}
                    >
                      Seed goal
                    </button>
                    <button className="generate-btn" type="button" disabled={impactLoading || !impactGoal.trim()} onClick={runImpactSimulation}>
                      {impactLoading ? "Simulating..." : "Simulate"}
                    </button>
                    {impactResult?.result?.summary && (
                      <button
                        className="generate-btn"
                        type="button"
                        onClick={() => {
                          setAgentGoal(String(impactResult.result.summary || ""));
                          proposeAgentPlanVariants();
                        }}
                      >
                        Use summary as agent goal
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <textarea
                    value={impactGoal}
                    onChange={(e) => setImpactGoal(e.target.value)}
                    rows={3}
                    placeholder="Macro goal to simulate..."
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(0,0,0,0.18)",
                      color: "#ffffff",
                      outline: "none",
                    }}
                  />
                </div>
                {impactError && <div style={{ marginTop: 8, fontSize: 12, color: "#ffb4b4" }}>{impactError}</div>}
                {impactResult?.result && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#cccccc" }}>
                    <div style={{ fontWeight: 900, color: "#ffffff" }}>Summary</div>
                    <div style={{ marginTop: 6 }}>{impactResult.result.summary}</div>
                  </div>
                )}
              </div>
              {(() => {
                const p = getActiveVisualPage();
                const meta = p && typeof p.metaDescription === "string" ? p.metaDescription : "";
                if (!meta) return null;
                return (
                  <div style={{ marginBottom: "14px", padding: "10px", borderRadius: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <div style={{ fontWeight: 800 }}>Meta description</div>
                      <button className="generate-btn" type="button" onClick={() => copyText(meta)}>Copy</button>
                    </div>
                    <div style={{ fontSize: "12px", color: "#cccccc", marginTop: "6px", whiteSpace: "pre-wrap" }}>{meta}</div>
                  </div>
                );
              })()}
            </>
          )}

          <div className="form-group">
            <label>Page Title</label>
            <input
              type="text"
              value={currentPage.title}
              onChange={(e) => setCurrentPage({ ...currentPage, title: e.target.value })}
              placeholder="Home, About, Contact, etc."
            />
          </div>
          <div className="form-group">
            <label>Page Content</label>
            <textarea
              value={currentPage.content}
              onChange={(e) => setCurrentPage({ ...currentPage, content: e.target.value })}
              placeholder="Describe what should be on this page..."
              rows="6"
            />
          </div>
          <div className="sections-builder">
            <label>Add Sections</label>
            <div className="section-buttons">
              <button onClick={() => addSection("header")}>Header</button>
              <button onClick={() => addSection("hero")}>Hero</button>
              <button onClick={() => addSection("features")}>Features</button>
              <button onClick={() => addSection("testimonials")}>Testimonials</button>
              <button onClick={() => addSection("contact")}>Contact Form</button>
              <button onClick={() => addSection("login")}>Login Section</button>
              <button onClick={() => addSection("payment")}>Payment Section</button>
            </div>
          </div>
          <div className="page-actions">
            <button onClick={savePage} className="save-btn">
              Save Page
            </button>
            <button onClick={() => setStep(4)} className="next-btn">
              Next: Configure Login & Payments
            </button>
          </div>
          {getOrderedPages(websiteData).length > 0 && (
            <div className="pages-list">
              <h3>Saved Pages:</h3>
              <small>Drag to reorder. Edit titles/content inline.</small>
              {(validation.errors.length > 0 || validation.warnings.length > 0) && (
                <div style={{ marginTop: "8px" }}>
                  {validation.errors.map((msg) => (
                    <div key={`err_${msg}`} style={{ color: "#ffb4b4", fontSize: "13px", marginBottom: "4px" }}>
                      {msg}
                    </div>
                  ))}
                  {validation.warnings.map((msg) => (
                    <div key={`warn_${msg}`} style={{ color: "#ffe7a3", fontSize: "13px", marginBottom: "4px" }}>
                      {msg}
                    </div>
                  ))}
                </div>
              )}
              <ul style={{ listStyle: "none", paddingLeft: 0, marginTop: "10px" }}>
                {getOrderedPages(websiteData).map((page) => (
                  <li
                    key={page.id}
                    draggable
                    onDragStart={() => setDraggingPageId(String(page.id))}
                    onDragEnd={() => setDraggingPageId(null)}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggingPageId) reorderPages(draggingPageId, page.id);
                      setDraggingPageId(null);
                    }}
                    style={{
                      marginBottom: "10px",
                      padding: "10px",
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: draggingPageId === String(page.id) ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <div style={{ fontWeight: 700, opacity: 0.9 }}>â†• Page</div>
                      <button type="button" className="cancel-btn" onClick={() => removePage(page.id)}>
                        Remove
                      </button>
                    </div>
                    <div className="form-group" style={{ marginTop: "8px" }}>
                      <label>Title</label>
                      <input
                        type="text"
                        value={String(page.title || "")}
                        onChange={(e) => updatePage(page.id, { title: e.target.value })}
                        placeholder="Page title"
                      />
                    </div>
                    <div className="form-group">
                      <label>Content</label>
                      <textarea
                        value={String(page.content || "")}
                        onChange={(e) => updatePage(page.id, { content: e.target.value })}
                        rows="4"
                      />
                    </div>
                    <div style={{ fontSize: "12px", color: "#cccccc" }}>
                      Sections: {Array.isArray(page.sections) ? page.sections.length : 0}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Login & Payment Configuration */}
      {step === 4 && (
        <div className="website-builder-step">
          <h2>Step 4: Login & Payment Integration</h2>
          <div className="integration-section">
            <h3>ðŸ” Login Integration</h3>
            <div className="integration-options">
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.google}
                  onChange={(e) => toggleAuthProvider("google", e.target.checked)}
                />
                Google {websiteData.integrationMode === "env" && websiteData.authProviders.google && websiteData.authConfig?.googleClientId ? "âœ…" : ""}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.facebook}
                  onChange={(e) => toggleAuthProvider("facebook", e.target.checked)}
                />
                Facebook {websiteData.integrationMode === "env" && websiteData.authProviders.facebook && websiteData.authConfig?.facebookAppId ? "âœ…" : ""}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.microsoft}
                  onChange={(e) => toggleAuthProvider("microsoft", e.target.checked)}
                />
                Microsoft {websiteData.integrationMode === "env" && websiteData.authProviders.microsoft && websiteData.authConfig?.microsoftClientId ? "âœ…" : ""}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.authProviders.apple}
                  onChange={(e) => toggleAuthProvider("apple", e.target.checked)}
                />
                Apple {websiteData.integrationMode === "env" && websiteData.authProviders.apple && websiteData.authConfig?.appleClientId ? "âœ…" : ""}
              </label>
            </div>
            {websiteData.integrationMode === "env" &&
              (websiteData.authProviders.google ||
                websiteData.authProviders.microsoft ||
                websiteData.authProviders.apple ||
                websiteData.authProviders.facebook) && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Redirect URL (OAuth callback)</label>
                  <input
                    type="text"
                    placeholder={`${window.location.origin}/auth/callback`}
                    value={websiteData?.authConfig?.redirectUrl || ""}
                    onChange={(e) =>
                      setWebsiteData((prev) => ({
                        ...prev,
                        authConfig: { ...(prev.authConfig || {}), redirectUrl: e.target.value },
                      }))
                    }
                  />
                  <small>Enter the redirect/callback URL you will register in each providerâ€™s OAuth settings.</small>
                </div>
              )}

            {websiteData.integrationMode === "env" && websiteData.authProviders.google ? (
              <div className="form-group">
                <label>Google Client ID</label>
                <input
                  type="text"
                  placeholder="xxxxx.apps.googleusercontent.com"
                  value={websiteData?.authConfig?.googleClientId || ""}
                  onChange={(e) =>
                    setWebsiteData((prev) => ({
                      ...prev,
                      authConfig: { ...(prev.authConfig || {}), googleClientId: e.target.value },
                    }))
                  }
                />
              </div>
            ) : null}
            {websiteData.integrationMode === "env" && websiteData.authProviders.microsoft ? (
              <div className="form-group">
                <label>Microsoft Client ID</label>
                <input
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={websiteData?.authConfig?.microsoftClientId || ""}
                  onChange={(e) =>
                    setWebsiteData((prev) => ({
                      ...prev,
                      authConfig: { ...(prev.authConfig || {}), microsoftClientId: e.target.value },
                    }))
                  }
                />
              </div>
            ) : null}
            {websiteData.integrationMode === "env" && websiteData.authProviders.apple ? (
              <div className="form-group">
                <label>Apple Client ID</label>
                <input
                  type="text"
                  placeholder="com.your.app"
                  value={websiteData?.authConfig?.appleClientId || ""}
                  onChange={(e) =>
                    setWebsiteData((prev) => ({
                      ...prev,
                      authConfig: { ...(prev.authConfig || {}), appleClientId: e.target.value },
                    }))
                  }
                />
              </div>
            ) : null}
            {websiteData.integrationMode === "env" && websiteData.authProviders.facebook ? (
              <div className="form-group">
                <label>Facebook App ID</label>
                <input
                  type="text"
                  placeholder="1234567890"
                  value={websiteData?.authConfig?.facebookAppId || ""}
                  onChange={(e) =>
                    setWebsiteData((prev) => ({
                      ...prev,
                      authConfig: { ...(prev.authConfig || {}), facebookAppId: e.target.value },
                    }))
                  }
                />
              </div>
            ) : null}
            {websiteData.integrationMode === "env" ? (
              <small style={{ display: "block", marginTop: 10 }}>
                These credentials are used only for generation and are not saved to Projects/Templates.
              </small>
            ) : null}
          </div>
          <div className="integration-section">
            <h3>ðŸ’³ Payment Integration</h3>
            <div className="integration-options">
              <label>
                <input
                  type="checkbox"
                  checked={websiteData.payments.enabled}
                  onChange={(e) => togglePaymentsEnabled(e.target.checked)}
                />
                Enable payments
              </label>
            </div>
            <div className="form-group">
              <label>Provider</label>
              <select
                value={websiteData.payments.provider}
                onChange={(e) =>
                  setWebsiteData({
                    ...websiteData,
                    payments: { ...websiteData.payments, provider: e.target.value },
                  })
                }
                disabled={!websiteData.payments.enabled}
              >
                <option value="stripe">Stripe</option>
                <option value="none">None</option>
              </select>
            </div>
            <div className="form-group">
              <label>Integration Mode</label>
              <select
                value={websiteData.integrationMode}
                onChange={(e) => setWebsiteData((prev) => ({ ...prev, integrationMode: String(e.target.value || "demo") }))}
              >
                <option value="demo">Demo placeholders (no real auth/payments)</option>
                <option value="env">I have keys (use env vars + wiring + stubs)</option>
              </select>
            </div>
            {websiteData.integrationMode === "env" && websiteData.payments.enabled && String(websiteData?.payments?.provider || "stripe") === "stripe" ? (
              <>
                <div className="form-group">
                  <label>Stripe Publishable Key</label>
                  <input
                    type="text"
                    placeholder="pk_live_... or pk_test_..."
                    value={websiteData?.paymentConfig?.stripe?.publishableKey || ""}
                    onChange={(e) =>
                      setWebsiteData((prev) => ({
                        ...prev,
                        paymentConfig: {
                          ...(prev.paymentConfig || {}),
                          stripe: { ...(prev.paymentConfig?.stripe || {}), publishableKey: e.target.value },
                        },
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Stripe Secret Key</label>
                  <input
                    type="password"
                    placeholder="sk_live_... or sk_test_..."
                    value={websiteData?.paymentConfig?.stripe?.secretKey || ""}
                    onChange={(e) =>
                      setWebsiteData((prev) => ({
                        ...prev,
                        paymentConfig: {
                          ...(prev.paymentConfig || {}),
                          stripe: { ...(prev.paymentConfig?.stripe || {}), secretKey: e.target.value },
                        },
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Stripe Price ID</label>
                  <input
                    type="text"
                    placeholder="price_..."
                    value={websiteData?.paymentConfig?.stripe?.priceId || ""}
                    onChange={(e) =>
                      setWebsiteData((prev) => ({
                        ...prev,
                        paymentConfig: {
                          ...(prev.paymentConfig || {}),
                          stripe: { ...(prev.paymentConfig?.stripe || {}), priceId: e.target.value },
                        },
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Stripe Webhook Secret (optional)</label>
                  <input
                    type="password"
                    placeholder="whsec_..."
                    value={websiteData?.paymentConfig?.stripe?.webhookSecret || ""}
                    onChange={(e) =>
                      setWebsiteData((prev) => ({
                        ...prev,
                        paymentConfig: {
                          ...(prev.paymentConfig || {}),
                          stripe: { ...(prev.paymentConfig?.stripe || {}), webhookSecret: e.target.value },
                        },
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Public URL</label>
                  <input
                    type="text"
                    placeholder={window.location.origin}
                    value={websiteData?.paymentConfig?.stripe?.publicUrl || ""}
                    onChange={(e) =>
                      setWebsiteData((prev) => ({
                        ...prev,
                        paymentConfig: {
                          ...(prev.paymentConfig || {}),
                          stripe: { ...(prev.paymentConfig?.stripe || {}), publicUrl: e.target.value },
                        },
                      }))
                    }
                  />
                </div>
                <small style={{ display: "block", marginTop: 10 }}>
                  These values are only used for generation and are not saved to Projects/Templates. The generated ZIP will include `.env` / `server/.env`.
                </small>
              </>
            ) : null}
            <div className="form-group">
              <label>Payment Plans</label>
              <select>
                <option>Free Plan</option>
                <option>Pro Plan - $20/month</option>
                <option>Ultra Plan - $200/month</option>
              </select>
            </div>
          </div>
          <button
            onClick={generateWebsite}
            disabled={loading || getOrderedPages(websiteData).length === 0 || validation.errors.length > 0}
            className="generate-btn"
          >
            {loading ? "Generating..." : "ðŸš€ Generate Website"}
          </button>
          {(validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div style={{ marginTop: "10px" }}>
              {validation.errors.map((msg) => (
                <div key={`gen_err_${msg}`} style={{ color: "#ffb4b4", fontSize: "13px", marginBottom: "4px" }}>
                  {msg}
                </div>
              ))}
              {validation.warnings.map((msg) => (
                <div key={`gen_warn_${msg}`} style={{ color: "#ffe7a3", fontSize: "13px", marginBottom: "4px" }}>
                  {msg}
                </div>
              ))}
            </div>
          )}

          {(generatedArtifacts || generatedHtmlDoc) && (
            <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                className="generate-btn"
                type="button"
                onClick={() => copyText(buildStandaloneHtml())}
              >
                Copy HTML
              </button>
              <button
                className="generate-btn"
                type="button"
                onClick={() => downloadTextFile("index.html", buildStandaloneHtml(), "text/html;charset=utf-8")}
              >
                Download HTML
              </button>
              <button
                className="generate-btn"
                type="button"
                onClick={async () => {
                  const tech = (websiteData?.techStack || "html").toLowerCase();
                  if (tech === "react") {
                    const files = buildViteReactZipFiles({
                      projectName: (websiteData?.name || "zolaid-react-app").toLowerCase().replace(/\s+/g, "-"),
                      appJsx: generatedArtifacts?.html || "",
                      appCss: generatedArtifacts?.css || "",
                      mainJsx: generatedArtifacts?.js || "",
                      extraFiles: generatedFiles || {},
                    });
                    await createZipAndDownload("zolaid-react-project.zip", files);
                  } else {
                    const htmlDoc = buildStandaloneHtml();
                    const files = {
                      ...buildStaticZipFiles({
                        htmlDoc,
                        css: generatedArtifacts?.css || "",
                        js: generatedArtifacts?.js || "",
                        includeSplitFiles: true,
                      }),
                      ...(generatedFiles || {}),
                    };
                    await createZipAndDownload("zolaid-website.zip", files);
                  }
                }}
              >
                Download ZIP
              </button>
              <button className="generate-btn" type="button" onClick={() => setShowDeployHelp(true)}>
                Deploy
              </button>
              {generatedArtifacts && (
                <>
                  <button className="generate-btn" type="button" onClick={() => copyText(generatedArtifacts.css || "")}>
                    Copy CSS
                  </button>
                  <button className="generate-btn" type="button" onClick={() => copyText(generatedArtifacts.js || "")}>
                    Copy JS
                  </button>
                  <button
                    className="generate-btn"
                    type="button"
                    onClick={() => {
                      downloadTextFile("styles.css", generatedArtifacts.css || "", "text/css;charset=utf-8");
                      downloadTextFile("app.js", generatedArtifacts.js || "", "text/javascript;charset=utf-8");
                    }}
                  >
                    Download CSS+JS
                  </button>
                </>
              )}
            </div>
          )}

          {(generatedArtifacts || generatedHtmlDoc) && (
            <div style={{ marginTop: "14px", height: "520px" }}>
              <LivePreview fullDoc={buildStandaloneHtml()} isVisible={true} frameDevice={true} />
            </div>
          )}

          {generatedInstructions && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", margin: "0 0 8px 0" }}>
                <h3 style={{ margin: 0 }}>Instructions</h3>
                <button className="generate-btn" type="button" onClick={() => copyText(generatedInstructions)}>
                  Copy
                </button>
              </div>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0, padding: "12px", borderRadius: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                {generatedInstructions}
              </pre>
            </div>
          )}

          {versions.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h3 style={{ margin: "0 0 8px 0" }}>Versions</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {versions.slice(0, 8).map((v) => (
                  <div
                    key={v.id}
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {v.websiteData?.name || "Website"} â€¢ {(v.websiteData?.techStack || "html").toUpperCase()}
                      </div>
                      <div style={{ fontSize: "12px", color: "#cccccc" }}>{new Date(v.ts).toLocaleString()}</div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button className="generate-btn" type="button" onClick={() => copyText(v.htmlDoc || buildStandaloneHtml())}>
                        Copy HTML
                      </button>
                      <button
                        className="generate-btn"
                        type="button"
                        onClick={async () => {
                          const tech = (v.websiteData?.techStack || "html").toLowerCase();
                          if (tech === "react") {
                            const files = buildViteReactZipFiles({
                              projectName: (v.websiteData?.name || "zolaid-react-app").toLowerCase().replace(/\s+/g, "-"),
                              appJsx: v.artifacts?.html || "",
                              appCss: v.artifacts?.css || "",
                              mainJsx: v.artifacts?.js || "",
                              extraFiles: v.files || {},
                            });
                            await createZipAndDownload("zolaid-react-project.zip", files);
                          } else {
                            const files = {
                              ...buildStaticZipFiles({
                                htmlDoc: v.htmlDoc || "",
                                css: v.artifacts?.css || "",
                                js: v.artifacts?.js || "",
                                includeSplitFiles: true,
                              }),
                              ...(v.files || {}),
                            };
                            await createZipAndDownload("zolaid-website.zip", files);
                          }
                        }}
                      >
                        Download ZIP
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDeployHelp && (
            <div className="website-builder-modal">
              <div className="website-builder-modal-content">
                <h2>Deploy your website</h2>
                <p>Pick a platform and follow the steps below.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", margin: "0 0 6px 0" }}>
                      <h3 style={{ margin: 0 }}>Vercel</h3>
                      <button
                        className="generate-btn"
                        type="button"
                        onClick={() =>
                          copyText(
                            (websiteData?.techStack || "html").toLowerCase() === "react"
                              ? `npm install\nnpm run build`
                              : `# Static site: no build required`
                          )
                        }
                      >
                        Copy commands
                      </button>
                    </div>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0, padding: "12px", borderRadius: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
{(websiteData?.techStack || "html").toLowerCase() === "react"
  ? `1) Download ZIP (React project)\n2) Unzip it\n3) Run:\n   npm install\n   npm run build\n4) Deploy the folder on Vercel (Import Project)\n\nBuild command: npm run build\nOutput directory: dist`
  : `1) Download ZIP (static)\n2) Unzip it\n3) Deploy index.html on Vercel (Static)\n\nTip: You can also drag-drop the folder into Netlify for static hosting.`}
                    </pre>
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", margin: "0 0 6px 0" }}>
                      <h3 style={{ margin: 0 }}>Netlify</h3>
                      <button
                        className="generate-btn"
                        type="button"
                        onClick={() =>
                          copyText(
                            (websiteData?.techStack || "html").toLowerCase() === "react"
                              ? `npm install\nnpm run build`
                              : `# Static site: no build required`
                          )
                        }
                      >
                        Copy commands
                      </button>
                    </div>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0, padding: "12px", borderRadius: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
{(websiteData?.techStack || "html").toLowerCase() === "react"
  ? `1) Download ZIP (React project)\n2) Unzip it\n3) Run:\n   npm install\n   npm run build\n4) Drag-drop the dist/ folder into Netlify\n\nBuild command: npm run build\nPublish directory: dist`
  : `1) Download ZIP (static)\n2) Unzip it\n3) Drag-drop the folder into Netlify\n\nPublish directory: /`}
                    </pre>
                  </div>
                </div>
                <button className="cancel-btn" type="button" onClick={() => setShowDeployHelp(false)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

